/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QWidgetMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QWidgetMetaData";
import {QPossibleValue} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QPossibleValue";
import {useField, useFormikContext} from "formik";
import colors from "qqq/assets/theme/base/colors";
import DynamicFormUtils, {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import DynamicSelect from "qqq/components/forms/DynamicSelect";
import MDTypography from "qqq/components/legacy/MDTypography";
import {WidgetScreenType} from "qqq/components/widgets/DashboardWidgets";
import
{
   addColumn, addGrid, addRow, addSubRow, buildOutputJson, cellFieldName, cellKey,
   collectGeneratedFieldNames, columnFieldName,
   DEFAULT_GRID_ID, emptyStructure, fieldDisplayLabel, findGridHeaderOverlaps,
   findMissingRequiredValues, findUniqueKeyViolations, getGridAxes, GridBuilderAxis,
   GridBuilderFieldNames, GridBuilderOrderBy, GridBuilderStructure, GridBuilderUniqueKey,
   gridFieldName, GridHeaderOverlap, isAxisSorted, isBlank, isGridBlank, isGridPrunable,
   isSubRowBlank, MissingRequiredValue, MULTIPLE_VALUE_DELIMITER,
   removeColumn, removeGrid, removeRow, removeSubRow, rowFieldName, seedValueForSelect, sortAxis, splitMultipleValue,
   structureFromWidgetData, UniqueKeyViolation, usesGridsDimension
} from "qqq/components/widgets/misc/gridBuilderModel";
import Widget from "qqq/components/widgets/Widget";
import usePossibleValueLabels from "qqq/utils/usePossibleValueLabels";
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as Yup from "yup";


/*******************************************************************************
 ** component props
 *******************************************************************************/
interface GridBuilderWidgetProps
{
   widgetMetaData: QWidgetMetaData;
   widgetData?: any;
   screen?: WidgetScreenType;
   onSaveCallback?: (values: { [name: string]: any }) => void;
   addSubValidations?: (name: string, validationScheme: Record<string, Yup.Schema>) => void;
   parentFormValues?: Record<string, any>;
}


/*******************************************************************************
 ** a sort rule as it arrives in widget metadata defaultValues
 *******************************************************************************/
interface GridBuilderSortRule
{
   orderBys?: GridBuilderOrderBy[];
   doAlert?: boolean;
   alertType?: string;
   alertMessage?: string;
   offerSortAction?: boolean;
   sortActionLabel?: string;
}


////////////////////////////////////////////////////////////////////////////////////
// the form-field font size used across record screens.  Set explicitly on the grid   //
// table, because the widget card it sits in uses a much larger size, and inheriting   //
// that makes line boxes taller than the controls in them.                             //
////////////////////////////////////////////////////////////////////////////////////
const GRID_FONT_SIZE = "0.875rem";

////////////////////////////////////////////////////////////////////////////////////
// the colour a CELL's value reads in view mode.  Taken from RecordScreenField,    //
// which renders every other value on a record screen, so a grid's data does not   //
// sit among those in a colour of its own.  Kept in sync with the value Typography //
// there, as the label element already is.                                        //
//                                                                                //
// Header values keep the darker default.  A header identifies a column, a row or  //
// a whole grid - it is nearer to a label than to data, and greying it flattens    //
// the distinction between what names a thing and what the thing holds.            //
////////////////////////////////////////////////////////////////////////////////////
const VIEW_VALUE_COLOR = "rgb(123, 128, 154)";

////////////////////////////////////////////////////////////////////////////////////
// the same three theme variables the child-record-list's DataGrid is styled from  //
// (see the .qqq-themed .MuiDataGrid-* rules in qqq-override-styles.css), so this  //
// table picks up an application's table theming instead of only agreeing with the //
// child list at the untouched defaults.  The fallbacks are those defaults.        //
////////////////////////////////////////////////////////////////////////////////////
const TABLE_HEADER_BACKGROUND = "var(--qqq-table-header-background-color, #f0f2f5)";
const TABLE_HEADER_TEXT_COLOR = "var(--qqq-table-header-text-color, #344767)";
const TABLE_BORDER_COLOR      = "var(--qqq-table-border-color, rgba(0, 0, 0, 0.12))";
const TABLE_ROW_HOVER_COLOR   = "var(--qqq-table-row-hover-color, rgba(0, 0, 0, 0.04))";

////////////////////////////////////////////////////////////////////////////////////
// the empty cell at the end of every row, which exists only to claim the width the //
// real columns do not want.  Without it, a table told to be 100% wide widens its   //
// columns to fill - so a two-column grid would render as two enormous columns      //
// rather than as two columns and empty space.                                      //
//                                                                                 //
// A width of 100% is a request, not an instruction: an auto-layout table hands out //
// leftover width in proportion to what each column asked for, so asking for all of //
// it is what makes this cell absorb effectively all of the slack.                  //
////////////////////////////////////////////////////////////////////////////////////
const SPACER_CELL_SX = {width: "100%", padding: 0};

////////////////////////////////////////////////////////////////////////////////
// how many blank required values to name individually before summarizing the rest //
////////////////////////////////////////////////////////////////////////////////
const MAX_LISTED_MISSING_VALUES = 8;

////////////////////////////////////////////////////////////////////////////////////
// minHeight is as necessary as height: the theme sets minHeight 40px on text-variant //
// buttons, which wins over height alone.  A 40px-tall button in a grid row makes the  //
// row 40px tall, and every control in it then centers 5px lower than the same control  //
// in a row without a button - which reads as the controls being out of alignment.      //
////////////////////////////////////////////////////////////////////////////////////
const iconButtonSX = {
   minWidth: "20px",
   width: "20px",
   minHeight: "20px",
   height: "20px",
   padding: 0,
   "& .MuiIcon-root": {fontSize: "1rem !important"},
};

const xIconButtonSX = {...iconButtonSX, color: colors.error.main};

const addIconButtonSX = {...iconButtonSX, color: colors.success.main};

////////////////////////////////////////////////////////////////////////////////
// the per-cell add and remove buttons are only shown while the pointer is in the    //
// cell they act on.  A grid of any size otherwise reads as a field of little icons   //
// with the data between them.                                                       //
////////////////////////////////////////////////////////////////////////////////
const HOVER_BUTTON_CLASS = "gridBuilderHoverButton";

////////////////////////////////////////////////////////////////////////////////
// applied to every grid cell, revealing the buttons inside it.                     //
//                                                                                 //
// visibility rather than display, so a hidden button still takes up its space and    //
// the cell does not change size when the pointer enters it.                          //
//                                                                                   //
// Transitioning visibility alongside opacity is what makes the fade work in both      //
// directions: going visible it switches at the start of the transition and the         //
// opacity fades up, and going hidden it stays visible until the fade has finished.     //
//                                                                                     //
// focus-within matters as much as hover: a visibility-hidden button cannot be           //
// focused, so without it a keyboard user could never reach these at all.  Tabbing       //
// into a field in the cell reveals them, and they are then in the tab order.            //
////////////////////////////////////////////////////////////////////////////////
const hoverRevealSX = {
   [`& .${HOVER_BUTTON_CLASS}`]: {
      visibility: "hidden",
      opacity: 0,
      transition: "opacity 150ms ease-in-out, visibility 150ms ease-in-out",
   },
   [`&:hover .${HOVER_BUTTON_CLASS}, &:focus-within .${HOVER_BUTTON_CLASS}`]: {
      visibility: "visible",
      opacity: 1,
   },
};

////////////////////////////////////////////////////////////////////////////////
// the "+ Add Temp. Band" / "+ Add TNT Rule" controls.  nowrap because the label is  //
// a configured noun that can be two or three words long: wrapped, it makes its       //
// header cell tall enough to stretch the whole header row, and reads as a column      //
// of words rather than as one control.                                                //
////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
// Bordered on all four sides, which is what every other input in the app is -        //
// DynamicFormField renders outlined, and DynamicSelect's default is outlined.  The    //
// grid started out standard (a bottom rule only), which read as a different kind of   //
// form from the rest of the screen.                                                   //
//                                                                                    //
// One constant for both control types, since a grid whose text inputs and selects      //
// disagreed about this would look like a mistake rather than a choice.                 //
////////////////////////////////////////////////////////////////////////////////
const GRID_INPUT_VARIANT: "standard" | "outlined" = "outlined";

////////////////////////////////////////////////////////////////////////////////
// the height of one line of controls in the grid.                                   //
//                                                                                   //
// 26px is the app's existing compact-outlined height, used by row editing            //
// (.qqq-row-edit-editing-row) and by DynamicSelect's own ".compactForm &" rules -      //
// which already apply here, since RecordScreen carries .compactForm while editing.     //
// Matching it rather than picking a number keeps the grid's controls the same size as   //
// the app's other dense forms.                                                        //
//                                                                                     //
// Also used to give a cell holding no controls the same height as one that does, so a   //
// lone add button lands on the same line as the inputs beside it rather than riding up  //
// against the top of its cell.                                                         //
////////////////////////////////////////////////////////////////////////////////
const CONTROL_LINE_HEIGHT = "26px";

////////////////////////////////////////////////////////////////////////////////
// compact-outlined sizing for the grid's plain text inputs.  DynamicSelect carries    //
// equivalent rules of its own for ".compactForm &"; a bare MUI TextField does not, so  //
// they are restated here rather than adding a .compactForm class inside the widget -   //
// a plain .compactForm selector leaks into every other widget on the record screen     //
// (see the note on .qqq-row-edit-editing-row in qqq-override-styles.css).              //
////////////////////////////////////////////////////////////////////////////////
const compactOutlinedInputSX = {
   "& .MuiOutlinedInput-root": {
      borderRadius: "3px",
      height: CONTROL_LINE_HEIGHT,
      minHeight: CONTROL_LINE_HEIGHT,
   },
   "& .MuiOutlinedInput-input": {
      fontSize: GRID_FONT_SIZE,
      padding: "3px 6px",
   },

   //////////////////////////////////////////////////////////////////////////////////////////
   // hide the spinner arrows a number input draws inside itself.  They are as wide as     //
   // the two or three digits these fields are configured to hold (a temperature is 4ch),  //
   // so they would leave no room for the value.  Suppressing the arrows keeps the rest of //
   // type=number - rejecting letters, and the numeric keypad on a touch device.           //
   //////////////////////////////////////////////////////////////////////////////////////////
   "& input[type=number]": {
      MozAppearance: "textfield",
   },
   "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
   },
};

////////////////////////////////////////////////////////////////////////////////
// an outlined input draws no background of its own, so whatever its cell is showing  //
// shows through it and the control reads as disabled.  White fills it back in, the    //
// way row editing does for the same reason.                                          //
//                                                                                   //
// Two things in this table put a color behind an input: the header cells' shading,    //
// and the row hover - which is why this belongs on every cell rather than only on     //
// the shaded ones.  A body cell is white anyway, so stating it changes nothing until  //
// the pointer arrives.                                                               //
//                                                                                    //
// The class is named twice deliberately.  DynamicSelect sets "background: initial" on  //
// .MuiInputBase-root from its own sx, which lands at the same specificity as a single-  //
// class rule from here - and a same-specificity tie goes to whichever emotion class is   //
// emitted last, which is not something to depend on.  Doubling the class outranks it     //
// outright, without an !important that a field's own CSS would then be unable to beat.  //
////////////////////////////////////////////////////////////////////////////////
const whiteInputBackgroundSX = {
   "& .MuiInputBase-root.MuiInputBase-root": {
      backgroundColor: "#ffffff",
   },

   //////////////////////////////////////////////////////////////////////////////////////
   // and the select's clear button, which appears on hover directly over the value it //
   // would clear - subRowSX narrows the space the input reserves for it precisely so  //
   // it can hang over the text, which is what buys a narrow cell room for a SKU.  An  //
   // opaque background is then what keeps that from reading as two overlapping words. //
   //                                                                                  //
   // Stated on the hover as well, because the IconButton theme pins that to           //
   // transparent (see components/iconButton.ts) and a pseudo-class rule from the      //
   // theme outranks a plain one from here - so white would drop out at the moment the //
   // pointer arrived.  Nothing is lost, since that theme rule is itself transparent.  //
   //////////////////////////////////////////////////////////////////////////////////////
   "& .MuiAutocomplete-clearIndicator": {
      backgroundColor: "#ffffff",
      "&:hover": {backgroundColor: "#ffffff"},
   },
};

////////////////////////////////////////////////////////////////////////////////
// the minHeight is the point of this: the theme puts 40px on text-variant buttons,  //
// so "+ Add Temp. Band" was a 40px control in a grid whose every other line is 29px, //
// stretching the header row and the add-row row around it.  28px replaces that floor  //
// rather than removing it - at its bare line box the button is 14px, which leaves the  //
// add-row row too cramped to read as a row of the same table.                          //
//                                                                                     //
// Still under a control line, so it is never what decides how tall a row is: in the      //
// header row it is absorbed by the taller inputs beside it, and in the add-row row it     //
// is the only thing setting the height.                                                  //
////////////////////////////////////////////////////////////////////////////////
const addAxisButtonSX = {
   textTransform: "none",
   padding: 0,
   whiteSpace: "nowrap",
   minHeight: "28px",
   lineHeight: 1.2,
};

////////////////////////////////////////////////////////////////////////////////
// tighten the space MUI reserves inside a sub-row's select for its clear and popup  //
// icons.  Sub-rows only: they hold the narrowest controls in the grid, so that        //
// reserved space is what truncates a SKU, while a header's select has room to spare.  //
//                                                                                    //
// Targets the MUI class names rather than the emitted rules, whose class names carry   //
// a hash of the style set and change whenever it does.  Naming MuiAutocomplete-root    //
// as well as the two state classes is what carries this past MUI's own rule, which     //
// matches three classes on the same element - equal specificity would leave it to       //
// stylesheet insertion order.                                                          //
////////////////////////////////////////////////////////////////////////////////
const subRowSX = {
   "& .MuiAutocomplete-root.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": {
      paddingRight: "24px",
   },
   "& .MuiAutocomplete-clearIndicator": {
      marginRight: "-10px",
   },
};


/*******************************************************************************
 ** read one of the three field lists out of widget metadata.  the validator
 ** plugin converts each list into frontend field metadata under a "frontend"
 ** prefixed name, which is what we prefer; the raw list is the fallback.
 *******************************************************************************/
function readFields(widgetMetaData: QWidgetMetaData, propertyName: string): QFieldMetaData[]
{
   const frontendPropertyName = "frontend" + propertyName.charAt(0).toUpperCase() + propertyName.slice(1);

   /////////////////////////////////////////////////////////////////////////////////////
   // each candidate is tested for being an array in its OWN right, rather than after //
   // the fallback has already picked one.  This is whatever the widget's             //
   // defaultValues held under that key, and a non-array would throw on .map during   //
   // render, taking the whole record screen with it - but a malformed frontend list  //
   // must not shadow a perfectly good raw one, which is what testing only the winner //
   // would do.  An empty list is what a widget declaring no fields for this axis     //
   // looks like anyway, so it is also the right answer when neither is usable.       //
   /////////////////////////////////////////////////////////////////////////////////////
   const candidates = [widgetMetaData.defaultValues?.get(frontendPropertyName), widgetMetaData.defaultValues?.get(propertyName)];
   const raw        = candidates.find(candidate => Array.isArray(candidate)) ?? [];

   return (raw.map((field: any) => new QFieldMetaData(field)));
}


/*******************************************************************************
 ** read a widget-metadata property that must be a list, as a list.
 **
 ** An absent property and a malformed one both read as empty, which is what a
 ** widget declaring nothing for it looks like.  Callers iterate these, and some
 ** iterate them inside a Yup test, where a throw is a submit that silently does
 ** nothing rather than a visible error.
 *******************************************************************************/
function readList<T>(widgetMetaData: QWidgetMetaData, propertyName: string): T[]
{
   const raw = widgetMetaData.defaultValues?.get(propertyName);
   return (Array.isArray(raw) ? raw as T[] : []);
}


/*******************************************************************************
 ** metadata numbers arrive as numbers or strings; null means "no limit"
 *******************************************************************************/
function readNumber(value: any, fallback: number | null): number | null
{
   if (value === null || value === undefined || value === "")
   {
      return (fallback);
   }
   return (Number(value));
}


/*******************************************************************************
 ** The CSS a field carries for use inside a grid, set in its metadata as
 ** GridBuilderFieldMetaData.  Merged over the widget's own defaults, so a field
 ** that specifies nothing keeps the default sizing, and a field that specifies
 ** only a width keeps the rest.
 *******************************************************************************/
function fieldSx(field: QFieldMetaData): Record<string, any>
{
   return (field.supplementalFieldMetaData?.get("gridBuilder")?.sx ?? {});
}


/*******************************************************************************
 ** whether a field is backed by a possible-value source, and so renders as a
 ** dropdown rather than as a text input.
 **
 ** The same condition DynamicFormUtils.addPossibleValueProps decides on, asked
 ** here so the widget can plan its label lookups without first building a
 ** dynamic form field for every field it has.
 *******************************************************************************/
function isPossibleValueField(field: QFieldMetaData): boolean
{
   return (!!(field.possibleValueSourceName || field.inlinePossibleValueSource));
}


/*******************************************************************************
 ** Whether a field holds several values rather than one, rendering as a
 ** multi-select.
 **
 ** An explicit opt-in, set per field via GridBuilderFieldMetaData.  Inferring it
 ** from "is a possible-value field" would deny any consumer a single-value
 ** dropdown in a grid header, which is a limitation no caller asked for.  It
 ** still requires a possible-value source, since there is nothing else for a
 ** multi-select to offer.
 *******************************************************************************/
function isMultipleValueField(field: QFieldMetaData): boolean
{
   return (field.supplementalFieldMetaData?.get("gridBuilder")?.isMultiple === true && isPossibleValueField(field));
}


/*******************************************************************************
 ** "At least 1 Temp. Band is required." / "At least 2 Temp. Bands are required."
 **
 ** The label is a configured noun, so it is pluralized by appending an s.
 *******************************************************************************/
function countShortfallMessage(minimum: number, label: string): string
{
   const noun = minimum === 1 ? label : label + "s";
   const verb = minimum === 1 ? "is" : "are";
   return (`At least ${minimum} ${noun} ${verb} required.`);
}


/*******************************************************************************
 ** "No Per Carton Override Grids exist."
 **
 ** What a read-only multi-grid widget says instead of rendering nothing.  With
 ** zero grids there is no table, no axis and no control, so the card came out as
 ** an empty box under its own heading - which reads as a screen that failed to
 ** load rather than as a record that has none of these.
 **
 ** Built from the configured gridLabel rather than a message of its own, so a
 ** consumer gets its own noun here for free, and cannot set one that disagrees
 ** with the label used by the count and location messages.  Pluralized by
 ** appending an s, as countShortfallMessage above does with the same nouns.
 *******************************************************************************/
function noGridsMessage(label: string): string
{
   return (`No ${label}s exist.`);
}


/*******************************************************************************
 ** one grid-level alert, animated in as it appears.
 **
 ** These alerts appear in response to something the user just did - a value
 ** typed out of order, a required value left blank at save - above a table they
 ** are already looking at.  Appearing instantly reads as the page jumping;
 ** growing into place shows where the new content came from, and moves the table
 ** down at a rate the eye can follow.
 **
 ** Mounts collapsed and expands in an effect rather than relying on a transition
 ** "appear" - the first paint is then guaranteed to be the closed state, so the
 ** animation always runs.
 *******************************************************************************/
function GridBuilderAlert({children}: { children: React.ReactNode }): JSX.Element
{
   const [isShown, setIsShown] = useState(false);

   useEffect(() =>
   {
      setIsShown(true);
   }, []);

   return (<Collapse in={isShown} timeout={250}>{children}</Collapse>);
}


/*******************************************************************************
 ** renderless Formik child.
 **
 ** This is how the grid reads form values without the container subscribing to
 ** them.  Subscribing in the container would re-render the whole table on every
 ** keystroke - which is the documented problem with RowBuilderWidget.  This
 ** component subscribes instead, renders nothing, keeps refs current, and
 ** notifies the container only so it can re-publish its JSON.
 *******************************************************************************/
function GridBuilderFormikBridge({valuesRef, setFieldValueRef, onValuesChanged, onSubmitAttempted}: {
   valuesRef: React.MutableRefObject<Record<string, any>>;
   setFieldValueRef: React.MutableRefObject<(name: string, value: any, shouldValidate?: boolean) => void>;
   onValuesChanged: () => void;
   onSubmitAttempted: () => void;
}): JSX.Element
{
   const formikContext = useFormikContext();

   valuesRef.current = (formikContext?.values ?? {}) as Record<string, any>;
   setFieldValueRef.current = formikContext?.setFieldValue;


   useEffect(() =>
   {
      onValuesChanged();
   }, [formikContext?.values]);

   /////////////////////////////////////////////////////////////////////////////////////
   // pressing Save is what earns the grid the right to complain about blank required //
   // values: before that, a grid the user has not finished filling in is not wrong   //
   // yet, and listing everything still to do reads as a screen full of errors.       //
   /////////////////////////////////////////////////////////////////////////////////////
   const submitCount = formikContext?.submitCount ?? 0;
   useEffect(() =>
   {
      if (submitCount > 0)
      {
         onSubmitAttempted();
      }
   }, [submitCount]);

   return (null);
}


/*******************************************************************************
 ** a field label, matching how the record screen renders one, so grid fields do
 ** not read as a different kind of form from the rest of the app.
 **
 ** Kept in sync with the labelElement in RecordScreenField.
 *******************************************************************************/
function GridBuilderFieldLabel({field, isEditable}: { field: QFieldMetaData; isEditable: boolean }): JSX.Element
{
   ///////////////////////////////////////////////////////////////////////////////
   // an empty label is how a widget opts a field out of being labeled - render //
   // nothing at all rather than a stray colon                                  //
   ///////////////////////////////////////////////////////////////////////////////
   if (isBlank(field.label))
   {
      return (null);
   }

   return (
      <Typography
         className="fieldLabel"
         variant="button"
         textTransform="none"
         fontWeight="bold"
         pr={1}
         color="rgb(52, 71, 103)"

         ///////////////////////////////////////////////////////////////////////////////////
         // the explicit fontSize matters as much as the weight: without it the label     //
         // inherits the widget card's 20px, whose line box is taller than the inputs and //
         // silently pushes a whole grid row's contents down.  RecordScreenField sets the //
         // same size for the same reason.                                                //
         ///////////////////////////////////////////////////////////////////////////////////
         sx={{flexShrink: 0, fontSize: GRID_FONT_SIZE, lineHeight: 1.2, whiteSpace: "nowrap"}}
      >
         {field.label}{isEditable && field.isRequired && <span style={{marginLeft: "1px"}}>*</span>}:
      </Typography>
   );
}


/*******************************************************************************
 ** one input within the grid - a header field or a cell sub-row field.
 **
 ** Binds with useField so a keystroke re-renders this input and nothing else.
 *******************************************************************************/
const GridBuilderInput = memo(function GridBuilderInput({field, name, isEditable, isMultiple, otherValues, displayValue, pickedDisplayValue, valueLabels, pickedValueLabels, viewValue, recordLabel, clearGeneration, onCommit, onPickDisplayValue, onPickValueLabels}: {
   field: QFieldMetaData;
   name: string;
   isEditable: boolean;
   isMultiple?: boolean;
   otherValues: Map<string, any>;
   displayValue?: string;
   pickedDisplayValue?: string;
   valueLabels?: Record<string, string>;
   pickedValueLabels?: Record<string, string>;
   viewValue?: any;
   recordLabel?: string;

   ////////////////////////////////////////////////////////////////////////////////////
   // folded into the select remount keys below, so that a prerequisite change which //
   // clears this field's value also clears what the select displays.  See its       //
   // declaration in the widget for why the rest of the key cannot do that.          //
   ////////////////////////////////////////////////////////////////////////////////////
   clearGeneration?: number;
   onCommit?: (name?: string, value?: any) => void;
   onPickDisplayValue?: (name: string, label: string) => void;
   onPickValueLabels?: (name: string, labels: Record<string, string>) => void;
}): JSX.Element
{
   const [formikField, meta, helpers] = useField(name);

   /////////////////////////////////////////////////////////////////////////////////////
   // the value a DynamicSelect below is seeded with - see seedValueForSelect for the //
   // race this guards (a select mounting before the widget's effect has published    //
   // its loaded values into Formik would otherwise capture undefined and stay empty, //
   // every select on the screen at once, intermittently, on load or view-to-edit).   //
   /////////////////////////////////////////////////////////////////////////////////////
   const valueToSeed = seedValueForSelect(formikField.value, viewValue);

   ////////////////////////////////////////////////////////////////////////////////////
   // for a multi-value input, the labels its several ids resolve to, joined - so a  //
   // read-only grid header reads "Large, Medium" rather than "1,2".  Only used when //
   // the backend sent no display value of its own for the whole set, which it       //
   // usually does.                                                                  //
   //                                                                                //
   // Read from the backend's values rather than from Formik for the same reason     //
   // view mode is (see below), and resolved id by id rather than by splitting a     //
   // joined display string: a label may itself contain the delimiter, so pairing    //
   // the two lists positionally is wrong the moment one does.                       //
   //                                                                                //
   // An id nothing has a label for shows as the id, so a partly-resolved set says   //
   // what it knows instead of hiding it; with NOTHING resolved this is undefined,   //
   // and the view-mode fallback below prints the raw value.                         //
   ////////////////////////////////////////////////////////////////////////////////////
   const multipleValueLabelsText = useMemo(() =>
   {
      if (!isMultiple)
      {
         return (undefined);
      }

      const ids    = splitMultipleValue(viewValue);
      const labels = ids.map(id => valueLabels?.[id] ?? pickedValueLabels?.[id]);

      if (labels.every(label => isBlank(label)))
      {
         return (undefined);
      }

      return (ids.map((id, index) => (isBlank(labels[index]) ? id : labels[index])).join(", "));
   }, [isMultiple, viewValue, valueLabels, pickedValueLabels]);

   //////////////////////////////////////////////////////////////////////////////////
   // the label to seed the dropdown with: what the backend sent, or - for a value //
   // the user chose in this session, which the backend has never seen - the label //
   // that came back with their choice.  Without the second one a remount of       //
   // DynamicSelect has nothing to show, since its displayed label otherwise lives //
   // only in its own uncontrolled Autocomplete state.                             //
   //                                                                              //
   // isBlank rather than ??, so a display value the backend sent as an EMPTY      //
   // STRING is read as absent rather than as a real label - it is not one, and in //
   // view mode it would render as nothing at all for a cell that has a value.     //
   //////////////////////////////////////////////////////////////////////////////////
   const labelToSeed = (!isBlank(displayValue) ? displayValue : (!isBlank(pickedDisplayValue) ? pickedDisplayValue : multipleValueLabelsText));

   const dynamicFormField = useMemo(() =>
   {
      const dynamicFormField = DynamicFormUtils.getDynamicField(field);

      //////////////////////////////////////////////////////////////////////////////
      // pass the display value in under the field's own name, which is the key   //
      // addPossibleValueProps reads, so an already-selected value renders as its //
      // label rather than as a blank dropdown                                    //
      //////////////////////////////////////////////////////////////////////////////
      const displayValues = new Map<string, string>();
      if (labelToSeed)
      {
         displayValues.set(field.name, labelToSeed);
      }

      DynamicFormUtils.addPossibleValuePropsToSingleField(dynamicFormField, field, null, null, displayValues);
      if (dynamicFormField.possibleValueProps)
      {
         /////////////////////////////////////////////////////////////////////////
         // the props are built from the field's own name, but this input lives //
         // under a namespaced name, so point them at the real one              //
         /////////////////////////////////////////////////////////////////////////
         dynamicFormField.possibleValueProps.fieldName = name;
      }
      return (dynamicFormField);
   }, [field, name, labelToSeed]);

   ////////////////////////////////////////////////////////////////////////////
   // view mode - a sub-row's recordLabel wins when the application set one, //
   // since only the application knows an itemId and a quantity read as a    //
   // SKU and a count                                                        //
   ////////////////////////////////////////////////////////////////////////////
   ///////////////////////////////////////////////////////////////////////////////////
   // View mode renders from what the backend sent, held in the widget's own state, //
   // NOT from Formik.  Saving rebuilds the record screen's initialValues from the  //
   // table's fields alone, which reinitializes Formik and drops these namespaced   //
   // grid names - so a Formik-sourced view mode goes blank right after a save.     //
   ///////////////////////////////////////////////////////////////////////////////////
   if (!isEditable)
   {
      const text = !isBlank(recordLabel) ? recordLabel : (!isBlank(labelToSeed) ? labelToSeed : (isBlank(viewValue) ? "" : String(viewValue)));
      return (<Box>{text}</Box>);
   }

   //////////////////////////////////////////////////////////////////////////////////////
   // a multi-value input - a branch of its own rather than props threaded through the //
   // single-value one below.  That one's markup is what every grid in the app renders //
   // today, down to the emotion classes its style objects generate, and a widget with //
   // no multi-value field has to reach it untouched.                                  //
   //                                                                                  //
   // The widget supplies the labels; the ids come from the live Formik value (with    //
   // the unwritten-field fallback of valueToSeed above), so a remount picks up        //
   // whatever is selected right now - including a value the user chose since the      //
   // last load, whose label is in pickedValueLabels.  An id nothing has a label for   //
   // shows as the id rather than as an empty chip.                                    //
   //////////////////////////////////////////////////////////////////////////////////////
   if (isMultiple && dynamicFormField.possibleValueProps)
   {
      const selectedValues = splitMultipleValue(valueToSeed)
         .map(id => new QPossibleValue({id: id, label: valueLabels?.[id] ?? pickedValueLabels?.[id] ?? String(id)}));

      return (
         <Box
            display="flex"
            alignItems="center"
            sx={{
               //////////////////////////////////////////////////////////////////////////////////////
               // wider than the single-value default, which is a floor for one identifier: this   //
               // control shows a chip per selection, and at 12ch a second chip has nowhere to go. //
               // A field still sets its own width in its GridBuilderFieldMetaData.                //
               //////////////////////////////////////////////////////////////////////////////////////
               width: "24ch",
               minWidth: "24ch",
               ...fieldSx(field),
               "& > *": {flexGrow: 1, minWidth: 0},

               ///////////////////////////////////////////////////////////////////////////////////////
               // MUI's filled chip is black with white text.  The query screen's any-of values     //
               // show the same control as outlined chips with dark text, and a grid header holding //
               // several values is the same kind of thing, so it reads the same way.               //
               //                                                                                   //
               // Stated here rather than in qqq-override-styles.css, where the query screen's own  //
               // override lives: adding this element's class to those selectors did not take       //
               // effect, and I could not establish why.  An sx rule is scoped to this subtree and  //
               // ships with the component, so it does not depend on that answer.  The cost is that //
               // these four values now exist in two places and can drift from the query screen's.  //
               ///////////////////////////////////////////////////////////////////////////////////////
               "& .MuiChip-root": {background: "none", color: "black", border: "1px solid gray"},

               ////////////////////////////////////////////////////////////////////////////////////
               // !important on BOTH, and the pair go together.  MUI's own rule for the resting  //
               // colour is as specific as this one and emotion injects MUI's later, so without  //
               // !important the tie goes to white-on-white - an x you cannot see until you      //
               // happen to hover it.  And once the resting colour is !important, a plain hover  //
               // rule can no longer beat it, however specific: !important outranks specificity. //
               ////////////////////////////////////////////////////////////////////////////////////
               "& .MuiChip-root .MuiChip-deleteIcon": {color: "gray !important"},
               "& .MuiChip-root .MuiChip-deleteIcon:hover": {color: "lightgray !important"},

               ///////////////////////////////////////////////////////////////////////////////////
               // the label's colour is set on the tag, not on the chip root - a chip root that //
               // says black with a tag that still says white is an unreadable chip, which is   //
               // what dropping this rule produced.                                             //
               ///////////////////////////////////////////////////////////////////////////////////
               "& .MuiAutocomplete-tag": {color: "#191919", background: "none"},
            }}
         >
            <DynamicSelect
               ///////////////////////////////////////////////////////////////////////////////////
               // same reasoning as the single-value key below, plus the resolved labels:       //
               // DynamicSelect captures its multiple-mode defaultValue ONCE at mount, from     //
               // initialValues, so labels that arrive from an async lookup after that would    //
               // never be shown.  Keying on them remounts the select exactly when they land.   //
               //                                                                               //
               // valueLabels only, NOT pickedValueLabels - the picked ones are recorded as the //
               // user chooses, and keying on those would remount the select out from under the //
               // click that set it.                                                            //
               ///////////////////////////////////////////////////////////////////////////////////
               key={`${name}-${viewValue ?? ""}-${displayValue ?? ""}-${JSON.stringify(valueLabels ?? null)}-${clearGeneration ?? 0}`}
               fieldPossibleValueProps={dynamicFormField.possibleValueProps}
               name={name}
               fieldLabel=""
               inForm={false}
               isMultiple
               initialValues={selectedValues}
               isEditable={true}
               otherValues={otherValues}
               variant={GRID_INPUT_VARIANT}
               initiallyOpen={false}
               useCase="form"
               onChange={(newValues: any) =>
               {
                  /////////////////////////////////////////////////////////////////////////////////////
                  // in multiple mode DynamicSelect hands back the raw array, so join the ids into   //
                  // the one value this input is bound to.  null when nothing is selected, matching  //
                  // the single-value branch - that is what isBlank reads, so a required grid header //
                  // left empty is still reported as missing.                                        //
                  //                                                                                 //
                  // The labels come with the array, so a value chosen here needs no lookup; they go //
                  // to the widget for the same reason a single pick's label does - the select's own //
                  // state is discarded by a remount.                                                //
                  /////////////////////////////////////////////////////////////////////////////////////
                  const values      = Array.isArray(newValues) ? newValues : [];
                  const joinedValue = values.length > 0 ? values.map(value => value.id).join(MULTIPLE_VALUE_DELIMITER) : null;

                  helpers.setValue(joinedValue);

                  const labels: Record<string, string> = {};
                  for (const value of values)
                  {
                     labels[value.id] = value.label;
                  }

                  onPickValueLabels?.(name, labels);
                  onCommit?.(name, joinedValue);
               }}
            />
         </Box>
      );
   }

   if (dynamicFormField.possibleValueProps)
   {
      return (
         ///////////////////////////////////////////////////////////////////////////////////
         // display:flex so this wrapper is exactly as tall as the select's input root,   //
         // with no extra line box of its own.  Every control in a grid row has the same  //
         // input-root height, so with equal-height flex items the row's center alignment //
         // puts them all on one line.  A plain block wrapper here added height that      //
         // shifted the select relative to the text input beside it.                      //
         //                                                                               //
         // DynamicSelect forces fullWidth on its Autocomplete, so this wrapper is what   //
         // decides how wide the control is.  The default is a floor that fits typical    //
         // identifiers; a field sets its own width in its GridBuilderFieldMetaData.      //
         //                                                                               //
         // The "& > *" rule is what makes the width take effect: this wrapper is a flex  //
         // container, and DynamicSelect's own Box is a flex item, which sizes to its     //
         // content rather than stretching unless told to grow.  Without it the wrapper   //
         // widens and the select does not, leaving the difference as empty space.        //
         ///////////////////////////////////////////////////////////////////////////////////
         <Box
            display="flex"
            alignItems="center"
            sx={{
               width: "12ch",
               minWidth: "12ch",
               ...fieldSx(field),
               "& > *": {flexGrow: 1, minWidth: 0},
            }}
         >
            <DynamicSelect
               /////////////////////////////////////////////////////////////////////////////
               // DynamicSelect is uncontrolled - it seeds its displayed value from       //
               // initialValue and initialDisplayValue once, at mount.  The grid re-seeds //
               // from the backend after a save, so without keying on those the select    //
               // would keep showing the label it mounted with.  Same reasoning as        //
               // RecordScreenField.                                                      //
               /////////////////////////////////////////////////////////////////////////////
               /////////////////////////////////////////////////////////////////////////////
               // key on the SEEDED value and label, not on the live Formik value - those //
               // change only when the widget re-seeds, so this never remounts while the  //
               // user is choosing something.                                             //
               //                                                                         //
               // pickedDisplayValue is deliberately NOT part of this key: it is recorded //
               // as the user chooses, and keying on it would remount the select out from //
               // under the click that set it.                                            //
               /////////////////////////////////////////////////////////////////////////////
               key={`${name}-${viewValue ?? ""}-${displayValue ?? ""}-${clearGeneration ?? 0}`}
               fieldPossibleValueProps={dynamicFormField.possibleValueProps}
               name={name}
               fieldLabel=""

               ///////////////////////////////////////////////////////////////////////////////
               // inForm renders an error-message Box below the input, which makes a select //
               // taller than the plain TextField beside it in the same cell.  the grid     //
               // reports errors at grid level and by marking the control, so the message   //
               // block is not wanted.  the value still reaches Formik through onChange     //
               // below - inForm only gates DynamicSelect writing it itself.                //
               ///////////////////////////////////////////////////////////////////////////////
               inForm={false}
               initialValue={valueToSeed}
               isEditable={true}
               otherValues={otherValues}
               variant={GRID_INPUT_VARIANT}
               initiallyOpen={false}
               useCase="form"
               /////////////////////////////////////////////////////////////////////////
               // DynamicSelect hands back a whole QPossibleValue, not the id.  store //
               // the id, matching what RecordScreenField does.                       //
               /////////////////////////////////////////////////////////////////////////
               ////////////////////////////////////////////////////////////////////////////
               // a select changes discretely, so choosing an option IS a commit - there //
               // is no half-typed state to wait out                                     //
               ////////////////////////////////////////////////////////////////////////////
               onChange={(newValue: any) =>
               {
                  const pickedId = newValue ? newValue.id : null;

                  helpers.setValue(pickedId);

                  /////////////////////////////////////////////////////////////////////////////////
                  // remember the label alongside the id.  DynamicSelect is uncontrolled, so the //
                  // text it displays lives in its own Autocomplete state and is gone the moment //
                  // it remounts - which a collapsed and re-opened section does.  the id would   //
                  // still be in Formik, leaving a filled cell looking empty.                    //
                  /////////////////////////////////////////////////////////////////////////////////
                  onPickDisplayValue?.(name, newValue ? newValue.label : null);
                  onCommit?.(name, pickedId);
               }}
            />
         </Box>
      );
   }

   const isNumeric = dynamicFormField.type === "number";

   //////////////////////////////////////////////////////////////////////////////////////
   // DynamicFormUtils derives an input type from the field's QFieldType.  Most of     //
   // them are types a TextField renders natively and the value-plus-onChange binding  //
   // below reads back correctly as a string - text, number, date, datetime-local,     //
   // time, password.                                                                  //
   //                                                                                  //
   // Four are not.  A BOOLEAN derives "checkbox", a BLOB derives "file", and a field  //
   // carrying the CODE_EDITOR adornment derives "ace" - bound this way none of them   //
   // works: a checkbox reads target.value instead of target.checked, so it never      //
   // shows the loaded value and ticking and un-ticking commit the same string,        //
   // leaving a flag that can be turned on but not off.                                //
   //                                                                                  //
   // DATE_TIME ("datetime-local") is rejected for a subtler reason: qqq sends it with //
   // a trailing Z, and the record screen converts on the way in and back out          //
   // (ValueUtils.formatDateTimeValueForForm, and                                      //
   // frontendLocalZoneDateTimeStringToUTCStringForBackend on save).  A grid does      //
   // neither, so a stored value fails the input's own value sanitization and renders  //
   // EMPTY, tabbing past it commits "" while Formik still holds the raw string, and a //
   // value the user types publishes as a naive local string beside backend values     //
   // carrying a zone.  Supporting it means doing both conversions here.               //
   //                                                                                  //
   // "date" and "time" do round-trip - their formats are exactly what those inputs    //
   // expect - and "password" is unremarkable.  Naming the type is better than         //
   // rendering a control that silently lies about what it holds.                      //
   //                                                                                  //
   // An UNRECOGNIZED QFieldType is not one of these - getDynamicField's default case  //
   // yields "text", so it renders as a text box and is allowed through.               //
   //                                                                                  //
   // Nothing declares such a field today - the backend's widget validator checks      //
   // names and duplicates but no types, so it would accept one - and supporting them  //
   // properly means the BooleanFieldSwitch / ace branches DynamicFormField already    //
   // has, worth doing when something actually asks for it rather than on speculation. //
   //                                                                                  //
   // Listed as types to REJECT rather than types to allow: an allowlist would have    //
   // silently broken date, time and password fields, which work today.                //
   //////////////////////////////////////////////////////////////////////////////////////
   if (dynamicFormField.type === "checkbox" || dynamicFormField.type === "file" || dynamicFormField.type === "ace" || dynamicFormField.type === "datetime-local")
   {
      return (
         <Box sx={{fontSize: "0.875rem", color: "#B00020"}}>
            {`Unsupported field type for a grid: ${dynamicFormField.type}`}
         </Box>
      );
   }

   return (
      <TextField
         name={name}
         value={formikField.value ?? ""}
         onChange={event => helpers.setValue(event.target.value)}

         ////////////////////////////////////////////////////////////////////////////////////
         // the input type DynamicFormUtils derived from the field's QFieldType - "number" //
         // for INTEGER and DECIMAL.  Without it every grid input was a text box that took //
         // letters happily and only failed at the backend.                                //
         ////////////////////////////////////////////////////////////////////////////////////
         type={dynamicFormField.type}

         /////////////////////////////////////////////////////////////////////////////////////
         // a number input changes its value on a wheel event while focused, so scrolling   //
         // the page over one silently edits it.  Blurring on wheel is how DynamicFormField //
         // handles the same problem.                                                       //
         /////////////////////////////////////////////////////////////////////////////////////
         onWheel={event =>
         {
            if (isNumeric)
            {
               (event.target as HTMLElement).blur();
            }
         }}

         ////////////////////////////////////////////////////////////////////////////////////
         // blur is the commit point for typed text.  alerts that judge a whole axis - out //
         // of order, duplicated - are computed from values captured here, so they do not  //
         // flash at half-typed values (typing 50 next to 40 is momentarily "5").          //
         //                                                                                //
         // it is also where a numeric cell stops being a string.  onChange stores what    //
         // the input gives it, which is always text - so without this a typed 40 would    //
         // publish "40" while a 40 loaded from the backend publishes 40, and the same     //
         // grid would serialize differently depending on where each value came from.      //
         // (that split is visible in the unique-key handling: gridBuilderModel treats 40  //
         // and "40" as different tuples.)                                                 //
         ////////////////////////////////////////////////////////////////////////////////////
         onBlur={event =>
         {
            const rawValue = event.target.value;

            ////////////////////////////////////////////////////////////////////////////////////
            // what this blur leaves in the form, which is the raw text unless a branch below //
            // replaces it.  Passed to the commit so it does not have to re-read a ref Formik //
            // has not updated yet - a blur is late enough that it usually would have, but    //
            // "usually" is how the selects came to report the previous value.                //
            ////////////////////////////////////////////////////////////////////////////////////
            let committedValue: any = rawValue;

            if (isNumeric)
            {
               if (isBlank(rawValue))
               {
                  committedValue = null;
                  helpers.setValue(null);
               }
               else if (!isNaN(Number(rawValue)))
               {
                  committedValue = Number(rawValue);
                  helpers.setValue(Number(rawValue));
               }
            }
            else
            {
               /////////////////////////////////////////////////////////////////////////////////
               // surrounding whitespace is never content.  Without this, a cell holding one  //
               // typed space is a REAL sub-row: published as " ", satisfying the required    //
               // check, and a unique-key tuple distinct from the empty one.  Trimming here   //
               // rather than in isBlank keeps the model's treatment of backend-loaded values //
               // and of sorting untouched.                                                   //
               /////////////////////////////////////////////////////////////////////////////////
               const trimmed = typeof rawValue === "string" ? rawValue.trim() : rawValue;
               if (trimmed !== rawValue)
               {
                  committedValue = isBlank(trimmed) ? null : trimmed;
                  helpers.setValue(committedValue);
               }
            }

            formikField.onBlur(event);
            onCommit?.(name, committedValue);
         }}
         /////////////////////////////////////////////////////////////////////////////////
         // no placeholder: when the field has a label it is already shown beside the   //
         // input, and repeating it inside only truncates ("Max T...").  when the label //
         // is empty there is nothing to put there anyway.  the field still needs to be //
         // identifiable to a screen reader, so the non-visual attributes fall back to  //
         // the field's name.                                                           //
         /////////////////////////////////////////////////////////////////////////////////
         title={field.label || field.name}
         inputProps={{"aria-label": field.label || field.name}}
         error={!!(meta.touched && meta.error)}
         variant={GRID_INPUT_VARIANT}
         ////////////////////////////////////////////////////////////////////////////////////
         // a numeric field gets a narrow default and everything else a wider one; a field //
         // overrides either through its GridBuilderFieldMetaData.  The field's own CSS is //
         // spread last so it wins over both the default width and the compact sizing.     //
         ////////////////////////////////////////////////////////////////////////////////////
         sx={{
            width: isNumeric ? "6ch" : "14ch",
            ...compactOutlinedInputSX,
            ...fieldSx(field),
         }}
      />
   );
});


/*******************************************************************************
 ** one cell - its sub-rows, and the controls to add and remove them
 *******************************************************************************/
const GridBuilderCellComponent = memo(function GridBuilderCellComponent({
   widgetName, gridId, rowId, columnId, subRowIds, cellFields, isEditable, minSubRowsPerCell, maxSubRowsPerCell,
   otherValues, displayValues, pickedDisplayValues, loadedValues, recordLabels, subRowLabel, onCommit, onPickDisplayValue, onAddSubRow, onRemoveSubRow
}: {
   widgetName: string;
   gridId: number;
   rowId: number;
   columnId: number;
   subRowIds: number[];
   cellFields: QFieldMetaData[];
   isEditable: boolean;
   minSubRowsPerCell: number;
   maxSubRowsPerCell: number | null;
   otherValues: Map<string, any>;
   displayValues: Record<string, string>;
   pickedDisplayValues: Record<string, string>;
   loadedValues: Record<string, any>;
   recordLabels: Record<string, string>;
   subRowLabel: string;
   onCommit: (name?: string, value?: any) => void;
   onPickDisplayValue: (name: string, label: string) => void;
   onAddSubRow: (rowId: number, columnId: number, gridId: number) => void;
   onRemoveSubRow: (rowId: number, columnId: number, subRowId: number, gridId: number) => void;
}): JSX.Element
{
   const mayAdd    = isEditable && (maxSubRowsPerCell === null || subRowIds.length < maxSubRowsPerCell);
   const mayRemove = isEditable && subRowIds.length > minSubRowsPerCell;

   if (subRowIds.length === 0)
   {
      return (
         //////////////////////////////////////////////////////////////////////////////////////
         // in edit mode, held to a full control line's height and centered in it, so this   //
         // cell's one small control sits on the same line as the inputs in the cells beside //
         // it - an empty cell is otherwise only as tall as a 20px button, and its contents  //
         // ride up against the top of the row.                                              //
         //                                                                                  //
         // NOT in view mode, where there are no inputs to line up with: every cell is a     //
         // line of text there, so holding this one to an input's height would make an empty //
         // cell TALLER than a full one, and a row of them taller than a row with content.   //
         //////////////////////////////////////////////////////////////////////////////////////
         <Box display="flex" alignItems="center" minHeight={isEditable ? CONTROL_LINE_HEIGHT : undefined}>
            {!isEditable && <Box color={colors.gray.main}>&mdash;</Box>}
            {/****************************************************************************
              * deliberately NOT hover-revealed, unlike every other add and remove button:
              * this is the only thing in an empty cell, so hiding it leaves a blank space
              * with nothing to say that anything can be put there.
              ****************************************************************************/}
            {mayAdd && (
               <Tooltip title={`Add ${subRowLabel}`} enterDelay={500}>
                  <Button sx={addIconButtonSX} onClick={() => onAddSubRow(rowId, columnId, gridId)}><Icon>add</Icon></Button>
               </Tooltip>
            )}
         </Box>
      );
   }

   return (
      <>
         {subRowIds.map((subRowId, index) =>
         {
            const recordLabel = recordLabels[cellFieldName("", rowId, columnId, subRowId, gridId, widgetName)];

            return (
               ///////////////////////////////////////////////////////////////////////////////////
               // baseline, not center: a label, a select and a text input in one line have     //
               // different heights and different internal padding, so centering (or bottom-    //
               // aligning) leaves their text sitting at different levels.  baseline aligns the //
               // text itself, which is what reads as aligned.                                  //
               ///////////////////////////////////////////////////////////////////////////////////
               <Box key={subRowId} display="flex" alignItems="center" gap="4px" mt={index === 0 ? 0 : "4px"} whiteSpace="nowrap" color={isEditable ? undefined : VIEW_VALUE_COLOR} sx={subRowSX}>
                  {
                     //////////////////////////////////////////////////////////////////////////
                     // in view mode a recordLabel, when the application supplied one,       //
                     // replaces the whole sub-row - it is the application's rendering of    //
                     // these fields together, so showing the fields as well would duplicate //
                     //////////////////////////////////////////////////////////////////////////
                     !isEditable && recordLabel
                        ? <Box>{recordLabel}</Box>
                        : cellFields.map(field => (
                           <React.Fragment key={field.name}>
                              <GridBuilderFieldLabel field={field} isEditable={isEditable} />
                              <GridBuilderInput
                                 field={field}
                                 name={cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)}
                                 isEditable={isEditable}
                                 otherValues={otherValues}
                                 displayValue={displayValues[cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)]}
                                 pickedDisplayValue={pickedDisplayValues[cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)]}
                                 viewValue={loadedValues[cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)]}
                                 onCommit={onCommit}
                                 onPickDisplayValue={onPickDisplayValue}
                              />
                           </React.Fragment>
                        ))
                  }
                  {mayRemove && (
                     <Tooltip title={`Remove ${subRowLabel}`} enterDelay={500}>
                        <Button className={HOVER_BUTTON_CLASS} sx={xIconButtonSX} onClick={() => onRemoveSubRow(rowId, columnId, subRowId, gridId)}><Icon>clear</Icon></Button>
                     </Tooltip>
                  )}
                  {mayAdd && index === subRowIds.length - 1 && (
                     <Tooltip title={`Add ${subRowLabel}`} enterDelay={500}>
                        <Button className={HOVER_BUTTON_CLASS} sx={addIconButtonSX} onClick={() => onAddSubRow(rowId, columnId, gridId)}><Icon>add</Icon></Button>
                     </Tooltip>
                  )}
               </Box>
            );
         })}
      </>
   );
});


/*******************************************************************************
 ** does this field's input render as type="number"?
 **
 ** Asked of DynamicFormUtils rather than of the field's QFieldType directly, so
 ** that it gives the same answer as the input itself, which reads the same
 ** derived type.
 *******************************************************************************/
function isNumericField(field: QFieldMetaData): boolean
{
   return (DynamicFormUtils.getDynamicField(field)?.type === "number");
}


/*******************************************************************************
 ** a copy of the values with the named ones turned from numeric-looking strings
 ** into numbers.  Anything already a number, blank, or not parseable is left
 ** exactly as it is - a value the user is part-way through typing is not the
 ** publisher's business to correct.
 *******************************************************************************/
function coerceNumericValues(values: Record<string, any>, numericNames: string[]): Record<string, any>
{
   let result = values;

   for (const name of numericNames)
   {
      const value = result[name];
      if (typeof value !== "string" || isBlank(value))
      {
         continue;
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // whitespace-only is nothing typed, not zero.  Number(" ") is 0, so reading it   //
      // as a number would publish {"quantity":0} for a cell whose type="number" input  //
      // renders as empty - inventing content out of an invisible keystroke rather than //
      // normalizing what was typed.  The trim pass ahead of this one already nulls     //
      // these, so it is a second line rather than the only one.                        //
      ////////////////////////////////////////////////////////////////////////////////////
      const trimmed = value.trim();
      const coerced = trimmed === "" ? null : (isNaN(Number(trimmed)) ? value : Number(trimmed));

      if (coerced !== value)
      {
         if (result === values)
         {
            result = {...values};
         }
         result[name] = coerced;
      }
   }

   return (result);
}


/*******************************************************************************
 ** Trim the string values held under the given names, blank becoming null.
 **
 ** The companion to coerceNumericValues, and here for the same reason: a text
 ** input's onBlur already trims, but a blur is not guaranteed to have happened.
 ** The record screen binds Cmd+S while focus is still in an input, so a cell
 ** holding one typed space could reach publish untrimmed - and a single space is
 ** not blank to isBlank, which made that sub-row a REAL one: published as
 ** {"itemCode":" "}, a child record built from an invisible keystroke, and any
 ** required sibling in it reported missing against a cell that looks empty,
 ** blocking the save with an alert the user cannot act on.
 **
 ** Trimming here rather than inside isBlank leaves the model's reading of
 ** backend-loaded values, and of sort order, exactly as it was.
 *******************************************************************************/
function trimStringValues(values: Record<string, any>, names: string[]): Record<string, any>
{
   let result = values;

   for (const name of names)
   {
      const value = result[name];
      if (typeof value === "string")
      {
         const trimmed = value.trim();
         if (trimmed !== value)
         {
            if (result === values)
            {
               result = {...values};
            }
            result[name] = isBlank(trimmed) ? null : trimmed;
         }
      }
   }

   return (result);
}


/*******************************************************************************
 ** Widget for editing a two-axis grid - the user defines column headers, row
 ** headers, and the contents of each cell.
 **
 ** Grid STRUCTURE (which columns, rows and sub-rows exist) lives in this
 ** component's state.  Grid VALUES live in the parent Formik context under names
 ** namespaced on three axes, which is what buys possible-value dropdowns, Yup
 ** validation, and per-input error placement.
 **
 ** On save the widget publishes its whole state as JSON under the metadata's
 ** outputFieldName.  The framework does not interpret that JSON - application
 ** code does, typically a table customizer using GridBuilderValues.
 *******************************************************************************/
export default function GridBuilderWidget({widgetMetaData, widgetData, screen, onSaveCallback, addSubValidations, parentFormValues}: GridBuilderWidgetProps): JSX.Element
{
   ///////////////////////////////
   // read metadata into locals //
   ///////////////////////////////
   //////////////////////////////////////////////////////////////////////////////////////
   // this widget's own name, which every input name it generates carries.  Two grid   //
   // widgets of the same kind on one screen otherwise allocate ids from counters that //
   // both start at 1 and generate the very same names for different inputs - each     //
   // reading the other's values, and each re-seed writing null over them.             //
   //////////////////////////////////////////////////////////////////////////////////////
   const widgetName = widgetMetaData.name;

   ////////////////////////////////////////////////////////////////////////////////////
   // empty for a widget presenting a single grid, which has nothing to identify it  //
   // by.  A non-empty list is also what opts the widget into the grids dimension at //
   // all - see usesGrids below.                                                     //
   ////////////////////////////////////////////////////////////////////////////////////
   const gridHeaderFields   = useMemo(() => readFields(widgetMetaData, "gridHeaderFields"), [widgetMetaData]);
   const columnHeaderFields = useMemo(() => readFields(widgetMetaData, "columnHeaderFields"), [widgetMetaData]);
   const rowHeaderFields    = useMemo(() => readFields(widgetMetaData, "rowHeaderFields"), [widgetMetaData]);
   const cellFields         = useMemo(() => readFields(widgetMetaData, "cellFields"), [widgetMetaData]);

   /////////////////////////////////////////////////////////////////////////////////////
   // the same four lists reduced to names, for the model functions that work in      //
   // names rather than in fields.  ONE of these, shared by everything that asks the  //
   // model a question about this widget - what to publish, and which grid's required //
   // fields may be left blank because it will not be published.  Two copies could    //
   // disagree, and those two answers disagreeing is a save that is blocked with      //
   // nothing on screen to explain it.                                                //
   /////////////////////////////////////////////////////////////////////////////////////
   const allFieldNames: GridBuilderFieldNames = useMemo(() => ({
      gridHeader: gridHeaderFields.map(field => field.name),
      columnHeader: columnHeaderFields.map(field => field.name),
      rowHeader: rowHeaderFields.map(field => field.name),
      cell: cellFields.map(field => field.name),
   }), [gridHeaderFields, columnHeaderFields, rowHeaderFields, cellFields]);

   //////////////////////////////////////////////////////////////////////////////////
   // and the same four lists narrowed to the fields whose inputs render as        //
   // type="number" - the ones publish coerces, decided by the same rule the input //
   // itself uses so the two cannot disagree about which fields those are.         //
   //////////////////////////////////////////////////////////////////////////////////
   const numericFieldNames: GridBuilderFieldNames = useMemo(() => ({
      gridHeader: gridHeaderFields.filter(isNumericField).map(field => field.name),
      columnHeader: columnHeaderFields.filter(isNumericField).map(field => field.name),
      rowHeader: rowHeaderFields.filter(isNumericField).map(field => field.name),
      cell: cellFields.filter(isNumericField).map(field => field.name),
   }), [gridHeaderFields, columnHeaderFields, rowHeaderFields, cellFields]);


   const minGrids          = readNumber(widgetMetaData.defaultValues?.get("minGrids"), 0);
   const maxGrids          = readNumber(widgetMetaData.defaultValues?.get("maxGrids"), null);
   const minColumns        = readNumber(widgetMetaData.defaultValues?.get("minColumns"), 0);
   const maxColumns        = readNumber(widgetMetaData.defaultValues?.get("maxColumns"), null);
   const minRows           = readNumber(widgetMetaData.defaultValues?.get("minRows"), 0);
   const maxRows           = readNumber(widgetMetaData.defaultValues?.get("maxRows"), null);
   const minSubRowsPerCell = readNumber(widgetMetaData.defaultValues?.get("minSubRowsPerCell"), 1);
   //////////////////////////////////////////////////////////////////////////////////
   // null means unlimited on every max.  the builder factory writes an explicit 1 //
   // for maxSubRowsPerCell, so a widget that never sets it still gets 1 - which   //
   // means a null or absent value here can only mean "explicitly unlimited".      //
   //////////////////////////////////////////////////////////////////////////////////
   const maxSubRowsPerCell = readNumber(widgetMetaData.defaultValues?.get("maxSubRowsPerCell"), null);

   ////////////////////////////////////////////////////////////////////////////////////
   // how many sub-rows a NEW cell starts with, which is not the same as the         //
   // minimum: a grid may allow a cell to be emptied to 0 while still starting new   //
   // cells with 1, so a new row is fillable without clicking into every cell first. //
   ////////////////////////////////////////////////////////////////////////////////////
   const initialSubRowsPerCell = readNumber(widgetMetaData.defaultValues?.get("initialSubRowsPerCell"), minSubRowsPerCell);

   ////////////////////////////////////////////////////////////////////////////////
   // how many columns and rows a brand-new grid starts with, defaulting to the  //
   // minimums - so a new grid never opens already violating its own count rules //
   ////////////////////////////////////////////////////////////////////////////////
   const initialColumns = readNumber(widgetMetaData.defaultValues?.get("initialColumns"), minColumns);
   const initialRows    = readNumber(widgetMetaData.defaultValues?.get("initialRows"), minRows);

   ////////////////////////////////////////////////////////////////////////////////////
   // and how many grids.  Zero seeds nothing - which is both a widget that predates //
   // the grids dimension and one whose grids are all optional.  A higher count is   //
   // topped up below, from however many the widget data described.                  //
   ////////////////////////////////////////////////////////////////////////////////////
   const initialGrids = readNumber(widgetMetaData.defaultValues?.get("initialGrids"), minGrids);

   ///////////////////////////////////////////////////////////////////////////////////////
   // how many grids this widget may hold, by its configuration alone.  That is what    //
   // decides where its grids live: a widget that may hold more than one gives every    //
   // one of them an allocated id, never DEFAULT_GRID_ID, so two grid widgets on one    //
   // screen cannot generate the same field names for different inputs.                 //
   //                                                                                   //
   // minGrids counts as well as initialGrids - a widget seeding none but requiring two //
   // still ends up with two.                                                           //
   ///////////////////////////////////////////////////////////////////////////////////////
   const expectedGridCount = Math.max(initialGrids, minGrids);

   /////////////////////////////////////////////////////////////////////////////
   // nouns for the four axes, composed into the add and remove controls - so //
   // "Temp. Band" yields "+ Add Temp. Band" and "Remove Temp. Band"          //
   /////////////////////////////////////////////////////////////////////////////
   const gridLabel: string   = widgetMetaData.defaultValues?.get("gridLabel") ?? "Grid";
   const columnLabel: string = widgetMetaData.defaultValues?.get("columnLabel") ?? "Column";
   const rowLabel: string    = widgetMetaData.defaultValues?.get("rowLabel") ?? "Row";
   const subRowLabel: string = widgetMetaData.defaultValues?.get("subRowLabel") ?? "Item";

   const isForRecordViewAndEditScreen = `${widgetMetaData.defaultValues?.get("isForRecordViewAndEditScreen")}` == "true";
   const isEditable                   = isForRecordViewAndEditScreen ? (screen === "recordEdit") : `${widgetMetaData.defaultValues?.get("isEditable")}` == "true";
   const outputFieldName              = widgetMetaData.defaultValues?.get("outputFieldName");

   const columnSortRule: GridBuilderSortRule = widgetMetaData.defaultValues?.get("columnSortRule");
   const rowSortRule: GridBuilderSortRule    = widgetMetaData.defaultValues?.get("rowSortRule");

   ////////////////////////////////////////////////////////////////////////////////////
   // read as arrays or not at all.  These four are ITERATED, and the three key      //
   // lists are iterated inside a Yup test - so a malformed one would throw during   //
   // validation rather than during render, which surfaces as a submit that silently //
   // does nothing instead of as a visible error.                                    //
   ////////////////////////////////////////////////////////////////////////////////////
   const columnHeaderUniqueKeys: GridBuilderUniqueKey[] = readList(widgetMetaData, "columnHeaderUniqueKeys");
   const rowHeaderUniqueKeys: GridBuilderUniqueKey[]    = readList(widgetMetaData, "rowHeaderUniqueKeys");
   const cellUniqueKeys: GridBuilderUniqueKey[]         = readList(widgetMetaData, "cellUniqueKeys");

   const prerequisiteFieldNames: string[] = readList(widgetMetaData, "prerequisiteFieldNames");
   const prerequisiteMessage: string      = widgetMetaData.defaultValues?.get("prerequisiteMessage") ?? "";

   ////////////////////////////////////////////////////////////
   // structure in state; values are read through these refs //
   ////////////////////////////////////////////////////////////
   const [structure, setStructure]         = useState<GridBuilderStructure>(() => emptyStructure(gridHeaderFields.length, expectedGridCount, widgetName));
   const [displayValues, setDisplayValues] = useState<Record<string, string>>({});
   const [recordLabels, setRecordLabels]   = useState<Record<string, string>>({});

   //////////////////////////////////////////////////////////////////////////////////////
   // labels for possible-values the user picked in this session, keyed by the input's //
   // namespaced name.  displayValues only ever holds what the backend sent, so        //
   // without these a value chosen since the last load has its label nowhere but       //
   // inside DynamicSelect's own uncontrolled state - see onPickDisplayValue.          //
   //////////////////////////////////////////////////////////////////////////////////////
   const [pickedDisplayValues, setPickedDisplayValues] = useState<Record<string, string>>({});

   const onPickDisplayValue = useCallback((name: string, label: string) =>
   {
      setPickedDisplayValues(current => ({...current, [name]: label}));
   }, []);

   /////////////////////////////////////////////////////////////////////////////////////
   // and the same thing for a multi-value input, which needs a label per id rather   //
   // than one label for the input: keyed by the input's namespaced name, then by id. //
   //                                                                                 //
   // Kept apart from pickedDisplayValues rather than folded into it, because folding //
   // would mean storing several labels in one string and splitting it back apart by  //
   // position - which is wrong for any label containing the delimiter, exactly the   //
   // trap that makes the backend's joined display value unusable here.               //
   //                                                                                 //
   // Merged rather than replaced, so a chip removed and put back needs no lookup.    //
   /////////////////////////////////////////////////////////////////////////////////////
   const [pickedValueLabels, setPickedValueLabels] = useState<Record<string, Record<string, string>>>({});

   const onPickValueLabels = useCallback((name: string, labels: Record<string, string>) =>
   {
      setPickedValueLabels(current => ({...current, [name]: {...current[name], ...labels}}));
   }, []);

   /////////////////////////////////////////////////////////////////////////////////
   // the values as the backend last sent them.  view mode renders from these     //
   // rather than from Formik, which gets reinitialized (without these namespaced //
   // names) every time the record is saved.                                      //
   /////////////////////////////////////////////////////////////////////////////////
   const [loadedValues, setLoadedValues] = useState<Record<string, any>>({});

   ////////////////////////////////////////////////////////////////////////////////////////
   // bumped every time a prerequisite change clears header values, and folded into the  //
   // header selects' remount keys.                                                      //
   //                                                                                    //
   // Those keys are otherwise built from the SEEDED value and label, so that a remount  //
   // cannot land mid-pick.  That leaves a value the user picked in this session with    //
   // nothing in its key to move: a pick writes pickedDisplayValues, which the key       //
   // deliberately excludes, while displayValues and loadedValues only ever hold what    //
   // the backend sent.  Clearing Formik and pruning the label maps therefore changes    //
   // no part of the key, the select reconciles rather than remounts, and DynamicSelect  //
   // goes on rendering the option the user clicked - it reads defaultValue once into    //
   // state and hands the Autocomplete no value prop, so the selection lives in MUI's    //
   // internal state where only a remount reaches it.                                    //
   //                                                                                    //
   // Symptom: pick a possible-value column header for client A, switch to client B, and //
   // the select still reads the client-A label while Formik holds null - so a save      //
   // publishes a blank header, and a required one is reported missing at a position     //
   // that visibly shows a value.                                                        //
   //                                                                                    //
   // A counter rather than the picked label itself, because it moves on a CLEAR and not //
   // on a pick, which is what excluding the picked label was protecting.                //
   //                                                                                    //
   // Cells are not keyed on this: a clear rebuilds their sub-rows with fresh ids, so    //
   // their field names change outright and they remount for that reason.                //
   ////////////////////////////////////////////////////////////////////////////////////////
   const [clearGeneration, setClearGeneration] = useState(0);

   ///////////////////////////////////////////////////////////////////////////////////
   // the values as of the last commit - a blur, a select, or a structural change.  //
   // Alerts that judge an entire axis are computed from this rather than from live //
   // values, so they do not appear and disappear while a value is half-typed.      //
   ///////////////////////////////////////////////////////////////////////////////////
   const [committedValues, setCommittedValues] = useState<Record<string, any>>({});

   /////////////////////////////////////////////////////////////////////////////////////
   // whether the grid has been populated from widget data yet.  Before that the      //
   // structure is empty, which looks exactly like a grid that violates its own count //
   // rules - so count alerts must stay quiet until there is something real to judge. //
   /////////////////////////////////////////////////////////////////////////////////////
   const [hasSeeded, setHasSeeded] = useState(false);

   //////////////////////////////////////////////////////////////////////////////////
   // whether the user has tried to save.  Blank required values are only reported //
   // after that - see the comment in the Formik bridge.                           //
   //////////////////////////////////////////////////////////////////////////////////
   const [submitAttempted, setSubmitAttempted] = useState(false);

   //////////////////////////////////////////////////////////////////////////////////
   // the alerts container, and the count of missing values as of the last render. //
   // A blocked save scrolls the alerts into view, which has to happen after the   //
   // render that produced them - hence reading the count from a ref in a timeout. //
   //////////////////////////////////////////////////////////////////////////////////
   const alertsRef        = useRef<HTMLDivElement>(null);
   const missingCountRef  = useRef<number>(0);

   const valuesRef       = useRef<Record<string, any>>({});
   const setFieldValueRef = useRef<(name: string, value: any, shouldValidate?: boolean) => void>(null);

   //////////////////////////////////////////////////////////////////////////////////
   // every field name the current structure generates, in a ref so that           //
   // handleCommit can trim them without taking the structure as a dependency - it //
   // is handed to every input, and giving it a new identity on each structural    //
   // change would re-render all of them.  Kept in step by the effect below.       //
   //                                                                              //
   // all of them, not only the text-input ones: trimming a numeric field is at    //
   // worst a no-op, and leaving those out meant a numeric input holding one space //
   // reached coerceNumericValues, where Number(" ") is 0.                         //
   //////////////////////////////////////////////////////////////////////////////////
   const generatedTrimmableNamesRef = useRef<string[]>([]);

   ///////////////////////////////////////////////////////////////////////////////
   // possible-value filters reference parent-record fields by name.  Pass ONLY //
   // those, not every form value: DynamicSelect refetches whenever otherValues //
   // changes, so passing everything would make a keystroke in an unrelated     //
   // record field invalidate the cached results in every dropdown in the grid. //
   ///////////////////////////////////////////////////////////////////////////////
   const prerequisiteValuesKey = JSON.stringify(prerequisiteFieldNames.map(name => parentFormValues?.[name]));
   const otherValues = useMemo(() =>
   {
      const map = new Map<string, any>();
      for (const name of prerequisiteFieldNames)
      {
         map.set(name, parentFormValues?.[name]);
      }
      return (map);
   }, [prerequisiteValuesKey]);

   const unmetPrerequisites = prerequisiteFieldNames.filter(name => isBlank(parentFormValues?.[name]));

   ///////////////////////////////////////////////////////////////////////////////////////
   // which grid header fields hold several values at once.  A grid header names the    //
   // thing a whole grid applies to, and that is routinely a set - one override grid    //
   // covering three carton types - so a possible-value field there is a multi-select.  //
   //                                                                                   //
   // Grid headers only.  Column headers, row headers and cell fields are unchanged,    //
   // which is what keeps every widget that predates the grids dimension rendering      //
   // exactly what it rendered before: those widgets have no grid header fields at all. //
   ///////////////////////////////////////////////////////////////////////////////////////
   const multipleValueGridHeaderFields = useMemo(() => gridHeaderFields.filter(field => isMultipleValueField(field)), [gridHeaderFields]);

   //////////////////////////////////////////////////////////////////////////////////////
   // the possible-value props each of those fields looks its labels up through, built //
   // once.  Under the FIELD's own name, not an input's namespaced one, so the hook's  //
   // per-field cache is shared by every grid.                                         //
   //////////////////////////////////////////////////////////////////////////////////////
   const labelLookupFields = useMemo(() =>
   {
      const result: Record<string, DynamicFormFieldDefinition> = {};
      for (const field of multipleValueGridHeaderFields)
      {
         const dynamicFormField = DynamicFormUtils.getDynamicField(field);
         DynamicFormUtils.addPossibleValuePropsToSingleField(dynamicFormField, field, null, null, new Map());
         result[field.name] = dynamicFormField;
      }
      return (result);
   }, [multipleValueGridHeaderFields]);

   ///////////////////////////////////////////////////////////////////////////////////////
   // every id a multi-value grid header currently holds, unioned across all grids and  //
   // grouped by field - so labels cost one lookup per FIELD rather than one per input. //
   //                                                                                   //
   // Read from the values the backend last sent, deliberately not from Formik: that    //
   // makes the set change only when the grid is re-seeded, so the selects below are    //
   // not re-keyed while the user is choosing something.  A value chosen in this        //
   // session arrives with its own label and needs no lookup at all.                    //
   ///////////////////////////////////////////////////////////////////////////////////////
   const idsToResolve = useMemo(() =>
   {
      const result: Record<string, any[]> = {};
      for (const field of multipleValueGridHeaderFields)
      {
         const ids: any[] = [];
         for (const gridId of structure.gridIds)
         {
            for (const id of splitMultipleValue(loadedValues[gridFieldName(field.name, gridId, widgetName)]))
            {
               if (!ids.includes(id))
               {
                  ids.push(id);
               }
            }
         }
         result[field.name] = ids;
      }
      return (result);
   }, [multipleValueGridHeaderFields, structure.gridIds, loadedValues]);

   /////////////////////////////////////////////////////////////////////////////////
   // the labels those ids resolve to, keyed by field name and then by id.  These //
   // seed the multi-selects and render a read-only grid header.                  //
   /////////////////////////////////////////////////////////////////////////////////
   const [valueLabels, setValueLabels] = useState<Record<string, Record<string, string>>>({});

   const {getDisplayValues} = usePossibleValueLabels({useCase: "form", otherValues});

   const idsToResolveKey = JSON.stringify(idsToResolve);

   //////////////////////////////////////////////////////////////////////////////////////
   // resolve those ids to labels, one request per field for every grid at once.       //
   //                                                                                  //
   // Keyed on the ids and nothing else - not on getDisplayValues, which is a fresh    //
   // function every render, and not on the labels this writes, which would loop.  The //
   // hook caches per field, so a re-seed that turns up no new id costs no request.    //
   //                                                                                  //
   // An inline possible-value source is answered from the field itself: its values    //
   // are already in the metadata, and the possible-values endpoint has no source name //
   // to look them up under.                                                           //
   //////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let isCancelled = false;

      const resolveLabels = async () =>
      {
         const resolved: Record<string, Record<string, string>> = {};

         for (const field of multipleValueGridHeaderFields)
         {
            const ids = idsToResolve[field.name] ?? [];
            if (ids.length === 0)
            {
               continue;
            }

            if (field.inlinePossibleValueSource)
            {
               const labels: Record<string, string> = {};
               for (const possibleValue of field.inlinePossibleValueSource)
               {
                  labels[possibleValue.id] = possibleValue.label;
               }
               resolved[field.name] = labels;
               continue;
            }

            resolved[field.name] = await getDisplayValues(labelLookupFields[field.name], ids);
         }

         if (!isCancelled && Object.keys(resolved).length > 0)
         {
            setValueLabels(current => ({...current, ...resolved}));
         }
      };

      resolveLabels();

      return () =>
      {
         isCancelled = true;
      };
   }, [idsToResolveKey]);

   /////////////////////////////////////////////////////////////////////////////////////
   // whether this widget presents grids as a dimension the user can see and act on,  //
   // rather than as the single anonymous grid every widget that predates them has.   //
   //                                                                                 //
   // The model decides this, and the whole grid-level UI is gated on it - the header //
   // row, the add and remove controls, the count alert, and naming a grid in a       //
   // message.  Gating on the predicate rather than on the count metadata is what     //
   // makes a widget that has not opted in render exactly what it rendered before:    //
   // that is then structurally true, instead of depending on minGrids and maxGrids   //
   // defaulting to values that happen to suppress the controls.                      //
   /////////////////////////////////////////////////////////////////////////////////////
   const usesGrids = usesGridsDimension(structure, gridHeaderFields.length);

   ////////////////////////////////////////////////////////////////////////////////////
   // every name this widget has EVER put into the parent form, added to as the      //
   // structure changes.  Read by the seeding effect below, which has to clear them  //
   // - declared before it so that on mount this holds the empty structure's names   //
   // (none) rather than trailing a render behind.                                   //
   //                                                                                //
   // Cumulative, not a snapshot of the current structure.  Removing a column, row   //
   // or grid deliberately leaves its values in the parent form (only removing a     //
   // sub-row clears them), so those names are gone from the structure while their   //
   // values are still there - and they are exactly the ones that collide, since     //
   // emptyStructure restarts the id counter at 1 on every seed and the next added   //
   // column can be handed a removed one's name.  A snapshot cannot see them, so the //
   // stale value would be published for a cell the user sees as blank.              //
   ////////////////////////////////////////////////////////////////////////////////////
   const generatedFieldNames = useRef<Set<string>>(new Set<string>());
   useEffect(() =>
   {
      collectGeneratedFieldNames(structure, allFieldNames).forEach(name => generatedFieldNames.current.add(name));
   }, [structure, allFieldNames]);

   ////////////////////////////////////////////////////////////////////////////////////
   // and the current structure's text names, for handleCommit's trim - a snapshot   //
   // rather than cumulative, since trimming a name no longer on screen would change //
   // a value nothing reads.                                                         //
   ////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      generatedTrimmableNamesRef.current = collectGeneratedFieldNames(structure, allFieldNames);
   }, [structure, allFieldNames]);

   ///////////////////////////////////////////////////////////////////////////////////////
   // NOTE on the values a removed column, row or grid leaves behind in the form:       //
   // they are deliberately left there.  buildOutputJson reads only names belonging to  //
   // the current structure, so nothing removed can reach the published grid.           //
   //                                                                                   //
   // They do go out in the record PATCH, which posts every key in the form values -    //
   // but that is a request-size matter, not a correctness one: the update maps values  //
   // to columns by starting from the TABLE's fields and keeping the ones the record    //
   // happens to carry (UpdateActionRecordSplitHelper), so a name that is not a field   //
   // on the table cannot reach the SQL.  A grid of any size already posts a namespaced //
   // key per cell per field whether or not anything was removed, so removals add more  //
   // of something already inert.                                                       //
   //                                                                                   //
   // Deleting them per-widget was tried and reverted.  Formik has no remove-key        //
   // operation, so it means setValues with the whole object rebuilt - which replaces   //
   // values wholesale and discarded the grid json this widget had published moments    //
   // earlier in the same effect flush, whereupon the publish dedupe below saw no       //
   // change and never re-sent it.  That saved the PREVIOUS client's grid onto the new  //
   // one.  Two grid widgets on a screen made it worse still: each rebuilt from the     //
   // same rendered snapshot, so the last one to write restored the other's keys.  If   //
   // this is ever worth doing, it belongs where the whole payload is assembled, not in //
   // one widget.                                                                       //
   ///////////////////////////////////////////////////////////////////////////////////////

   ///////////////////////////////////////////////////////////////////////////////
   // seed from the widget data.  keyed on screen as well as widgetData so that //
   // leaving edit mode without saving resets to what the backend last sent -   //
   // which makes cancel revert, and guarantees view mode only ever renders     //
   // backend-provided records (the ones that carry recordLabels).              //
   ///////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      const seeded = structureFromWidgetData(widgetData, gridHeaderFields.length, expectedGridCount, widgetName);

      ////////////////////////////////////////////////////////////////////////////////////////
      // an empty grid on an editable screen starts at its initial counts, so the user is   //
      // not shown "at least 1 column is required" before doing anything.  only when there  //
      // is nothing at all - a saved grid is shown exactly as it was saved, and a read-only //
      // screen never fabricates rows.                                                      //
      //                                                                                    //
      // "nothing at all" is no grids for a widget whose grids carry allocated ids, and an  //
      // empty single grid for every other widget.  The model already decided which of the  //
      // two this is, so it is asked rather than restated - a second reading of the same    //
      // signal here is how the structure and the names it generates would come apart.      //
      ////////////////////////////////////////////////////////////////////////////////////////
      let structureToUse = seeded.structure;

      const holdsNothing = seeded.gridIdsAreAllocated
         ? structureToUse.gridIds.length === 0
         : (structureToUse.columnIds.length === 0 && structureToUse.rowIds.length === 0);

      if (isEditable && holdsNothing)
      {
         ///////////////////////////////////////////////////////////////////////////////////////
         // the columns and rows of the one grid such a widget has.  A widget whose grids     //
         // carry allocated ids has no grid to put them in yet - its columns and rows arrive  //
         // with the grids the loop below adds, one set per grid - and seeding them here      //
         // would write them into DEFAULT_GRID_ID, a grid it does not hold, on the very names //
         // it must stay off.                                                                 //
         ///////////////////////////////////////////////////////////////////////////////////////
         if (!seeded.gridIdsAreAllocated)
         {
            for (let i = 0; i < initialColumns; i++)
            {
               structureToUse = addColumn(structureToUse, initialSubRowsPerCell);
            }
            for (let i = 0; i < initialRows; i++)
            {
               structureToUse = addRow(structureToUse, initialSubRowsPerCell);
            }
         }

         ////////////////////////////////////////////////////////////////////////////////////
         // and the grids the widget data did not already describe, each starting with the //
         // widget's initial columns and rows.  A widget that says nothing about grids has //
         // initialGrids of 0, so this loop does not run at all.                           //
         ////////////////////////////////////////////////////////////////////////////////////
         while (structureToUse.gridIds.length < initialGrids)
         {
            structureToUse = addGrid(structureToUse, initialColumns, initialRows, initialSubRowsPerCell);
         }
      }

      setStructure(structureToUse);
      setDisplayValues(seeded.displayValues);
      setRecordLabels(seeded.recordLabels);
      setLoadedValues(seeded.values);

      ////////////////////////////////////////////////////////////////////////////////
      // a re-seed replaces what is on screen with what the backend sent, so labels //
      // remembered from the previous session's picks no longer describe any cell   //
      ////////////////////////////////////////////////////////////////////////////////
      setPickedDisplayValues({});
      setPickedValueLabels({});

      ///////////////////////////////////////////////////////////////////////////////////
      // freshly loaded values count as committed - nothing is half-typed yet - but    //
      // trimmed all the same, so that what the alerts read of BACKEND-loaded data     //
      // matches what they read of it after the first commit.  Untrimmed here, a       //
      // stored trailing space made a duplicate invisible until the user pressed Save, //
      // at which point the alert appeared for something they had not touched.         //
      ///////////////////////////////////////////////////////////////////////////////////
      setCommittedValues(trimStringValues(seeded.values, collectGeneratedFieldNames(structureToUse, allFieldNames)));
      setHasSeeded(true);

      //////////////////////////////////////////////////////////////////////////////
      // a re-seed is a fresh start - either a save went through or edit mode was //
      // left, so any complaint about the previous attempt no longer applies      //
      //////////////////////////////////////////////////////////////////////////////
      setSubmitAttempted(false);

      //////////////////////////////////////////////////////////////////////////////
      // re-seeding means whatever was published before no longer describes what  //
      // is on screen, so let the next publish through rather than suppressing it //
      // as a duplicate                                                           //
      //////////////////////////////////////////////////////////////////////////////
      lastPublishedJson.current = null;

      /////////////////////////////////////////////////////////////////////////////////////
      // write the seed into the parent form - but only on an editable screen.  A widget //
      // with includeOnRecordEditScreen renders inside Formik in VIEW mode too, and      //
      // writing there marks the view-mode form dirty for values nobody edited.  This    //
      // effect re-runs on `screen`, so entering edit seeds them then.  (publish is      //
      // gated on isEditable for the same reason.)                                       //
      //                                                                                 //
      // Every name this widget has generated so far is cleared first.  Only the names   //
      // the backend sent get written, and emptyStructure restarts the id counter at 1   //
      // on every seed - so without this, a value typed into a cell that was then        //
      // removed can land on a freshly-allocated input that reuses its name, and get     //
      // published for a cell the user sees as blank.                                    //
      /////////////////////////////////////////////////////////////////////////////////////
      if (isEditable)
      {
         generatedFieldNames.current.forEach(name =>
         {
            if (!(name in seeded.values))
            {
               setFieldValueRef.current?.(name, null, false);
            }
         });

         for (const name of Object.keys(seeded.values))
         {
            setFieldValueRef.current?.(name, seeded.values[name], false);
         }
      }
   }, [widgetData, screen]);

   /***************************************************************************
    * collect a header's name only when it was drawn from a list the prerequisite
    * scoped - a possible-value field.  A literal header, like a temperature or
    * a quantity, means the same thing whichever client is selected, and
    * throwing one away would discard work for no reason.
    ***************************************************************************/
   const collectPrerequisiteScopedNames = useCallback((fields: QFieldMetaData[], nameOfField: (field: QFieldMetaData) => string): string[] =>
   {
      return (fields.filter(field => isPossibleValueField(field)).map(field => nameOfField(field)));
   }, []);

   ///////////////////////////////////////////////////////////////////////////////////
   // clear the contents scoped to a prerequisite when that prerequisite changes -  //
   // every cell, and any header drawn from a possible-value list, since a list     //
   // filtered by the old value cannot vouch for a selection under the new one.     //
   //                                                                               //
   // Headers used to be left alone wholesale, which was right while they were only //
   // literals: a max temperature is a max temperature for every client.  A header  //
   // naming a client's carton types is not.                                        //
   ///////////////////////////////////////////////////////////////////////////////////
   const previousPrerequisiteValues = useRef<string>(null);
   useEffect(() =>
   {
      ////////////////////////////////////////////////////////////////////////////////////
      // only clear when switching from one set of prerequisite values to a DIFFERENT   //
      // set that is also fully populated.  going from nothing-selected to selected is  //
      // the normal first-time path: there is nothing meaningful to clear, and clearing //
      // there discards the sub-rows the grid just seeded (and anything typed since).   //
      ////////////////////////////////////////////////////////////////////////////////////
      const hadAllPrerequisites = previousPrerequisiteValues.current !== null
         && !prerequisiteFieldNames.some((name, index) => isBlank(JSON.parse(previousPrerequisiteValues.current)[index]));

      if (hadAllPrerequisites && previousPrerequisiteValues.current !== prerequisiteValuesKey)
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // the names to clear are collected HERE, off the structure this render is         //
         // looking at, and cleared in the effect body below - NOT from inside the          //
         // setStructure updater.  React invokes an updater during the render phase (and    //
         // twice under StrictMode), so calling the parent form's setFieldValue in there    //
         // is a state update on another component mid-render: React logs about it, and     //
         // under concurrent rendering the writes can be applied twice or thrown away.      //
         // The updater below is left pure for the same reason publish was taken out of it. //
         /////////////////////////////////////////////////////////////////////////////////////
         const namesToClear: string[] = [];

         //////////////////////////////////////////////////////////////////////////////////
         // every grid's cells, not just the first one's - the prerequisite that scoped  //
         // these selections scopes the whole widget, so a stale choice in a second grid //
         // is exactly as wrong as one in the first.                                     //
         //////////////////////////////////////////////////////////////////////////////////
         for (const gridId of structure.gridIds)
         {
            const axes = getGridAxes(structure, gridId);

            for (const rowId of axes.rowIds)
            {
               for (const columnId of axes.columnIds)
               {
                  for (const subRowId of axes.subRowIds[cellKey(rowId, columnId)] ?? [])
                  {
                     for (const field of cellFields)
                     {
                        namesToClear.push(cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName));
                     }
                  }
               }

               namesToClear.push(...collectPrerequisiteScopedNames(rowHeaderFields, field => rowFieldName(field.name, rowId, gridId, widgetName)));
            }

            for (const columnId of axes.columnIds)
            {
               namesToClear.push(...collectPrerequisiteScopedNames(columnHeaderFields, field => columnFieldName(field.name, columnId, gridId, widgetName)));
            }

            namesToClear.push(...collectPrerequisiteScopedNames(gridHeaderFields, field => gridFieldName(field.name, gridId, widgetName)));
         }

         for (const name of namesToClear)
         {
            setFieldValueRef.current?.(name, null, false);
         }

         /////////////////////////////////////////////////////////////////////////////////////
         // the label maps have to lose these names too, or a cleared possible-value HEADER //
         // goes on showing the old label.  A select's remount key is name + loadedValues + //
         // displayValues (deliberately not the live Formik value, so a remount cannot land //
         // mid-pick), and DynamicSelect captures its selection once at mount.  Clearing    //
         // Formik alone changes no part of that key, so the select is reconciled rather    //
         // than remounted and keeps rendering what the previous prerequisite resolved to - //
         // e.g. a column header still reading "Large" for a client that no longer offers   //
         // it, published blank and reported missing at a position showing a value.  Cells  //
         // escape this only because their sub-rows are rebuilt below with fresh ids, which //
         // changes their names outright.                                                   //
         //                                                                                 //
         // pruning the maps is not enough on its own, though.  It moves the key only for a //
         // label the BACKEND sent; a value the user picked in this session was never in    //
         // either map, so there is nothing to prune and the key does not move.  That is    //
         // what clearGeneration is for - see its declaration.                              //
         /////////////////////////////////////////////////////////////////////////////////////
         if (namesToClear.length > 0)
         {
            setClearGeneration(current => current + 1);

            const clearedNames = new Set(namesToClear);
            const withoutClearedNames = (previous: Record<string, any>) =>
            {
               const result: Record<string, any> = {};
               for (const key of Object.keys(previous))
               {
                  if (!clearedNames.has(key))
                  {
                     result[key] = previous[key];
                  }
               }
               return (result);
            };

            setDisplayValues(withoutClearedNames);
            setLoadedValues(withoutClearedNames);
            setPickedDisplayValues(withoutClearedNames);
            setPickedValueLabels(withoutClearedNames);
         }

         setStructure(current =>
         {
            let result = current;

            ////////////////////////////////////////////////////////////////////////
            // iterated from `current`, since each mutation below returns a fresh //
            // structure that `result` accumulates                                //
            ////////////////////////////////////////////////////////////////////////
            for (const gridId of current.gridIds)
            {
               const axes = getGridAxes(current, gridId);

               for (const rowId of axes.rowIds)
               {
                  for (const columnId of axes.columnIds)
                  {
                     for (const subRowId of axes.subRowIds[cellKey(rowId, columnId)] ?? [])
                     {
                        result = removeSubRow(result, rowId, columnId, subRowId, gridId);
                     }
                     /////////////////////////////////////////////////////////////////////
                     // a cleared cell is effectively a new one, so re-seed it with the //
                     // starting count rather than the bare minimum                     //
                     /////////////////////////////////////////////////////////////////////
                     for (let i = 0; i < initialSubRowsPerCell; i++)
                     {
                        result = addSubRow(result, rowId, columnId, gridId);
                     }
                  }
               }
            }
            return (result);
         });
      }
      previousPrerequisiteValues.current = prerequisiteValuesKey;
   }, [prerequisiteValuesKey]);


   /***************************************************************************
    * publish the grid as JSON under outputFieldName.
    *
    * Sorts by the declared rules first, so the blob's column order is already
    * the order the backend will store - which is why no backend sort is needed.
    *
    * Publishing writes to the PARENT form, which changes the Formik values
    * object, which is exactly what the bridge below watches - so publishing
    * unconditionally is an infinite render loop.  The last-published JSON is
    * tracked so an unchanged grid publishes nothing and the cycle terminates.
    ***************************************************************************/
   const lastPublishedJson = useRef<string>(null);

   const publish = useCallback((toPublish: GridBuilderStructure) =>
   {
      if (!outputFieldName)
      {
         return;
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // nothing to publish before the grid has been seeded - the Formik bridge's mount //
      // effect runs before the parent's seeding effect (child effects run first), so   //
      // without this the first publish writes an empty grid into the parent form.  And //
      // on a read-only screen there is nothing to write at all: key order and number   //
      // formatting alone would differ from the stored JSON and mark the form dirty.    //
      ////////////////////////////////////////////////////////////////////////////////////
      if (!isEditable || !hasSeeded)
      {
         return;
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // and nothing to publish behind an unmet prerequisite, for the same reason the   //
      // validations effect registers nothing there: none of these inputs are on screen //
      // and the seeding effect has fabricated blank columns and rows behind the        //
      // "select a client first" message.  Publishing them writes a phantom grid the    //
      // user never saw into outputFieldName, and marks the form dirty for it.          //
      //                                                                                //
      // but the output field is CLEARED on the way in, rather than simply left alone.  //
      // Returning without touching it strands whatever was published while the         //
      // prerequisite was still met: pick client A, fill the grid, then clear the       //
      // client, and the table is replaced by the "select a client first" message while //
      // A's json sits in the form - saved by sanitizeFormValuesForSave, which posts    //
      // every value it is given, against a client no longer selected and a grid no     //
      // longer on screen.  Nothing blocks it either, since the validations effect      //
      // registers an empty group in this state.  Switching A -> B never showed this,   //
      // because both prerequisites are met and publish runs on the blanked cells; only //
      // A -> blank reaches here with something already published.                      //
      ////////////////////////////////////////////////////////////////////////////////////
      if (unmetPrerequisites.length > 0)
      {
         if (lastPublishedJson.current !== null)
         {
            lastPublishedJson.current = null;
            onSaveCallback?.({[outputFieldName]: null});
         }
         return;
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // numbers are coerced HERE, and not only in a text input's onBlur, so that what  //
      // is published does not depend on a blur having happened.  Pressing Enter in an  //
      // input submits the record form without blurring it, and the raw string would go //
      // out as "40" for a typed value while a backend-loaded 40 goes out as a number - //
      // the same grid serializing differently depending on where each value came from. //
      ////////////////////////////////////////////////////////////////////////////////////
      const values = coerceNumericValues(
         trimStringValues(valuesRef.current, collectGeneratedFieldNames(toPublish, allFieldNames)),
         collectGeneratedFieldNames(toPublish, numericFieldNames));

      //////////////////////////////////////////////////////////////////////////////
      // each grid is sorted on its OWN axis ids.  Sorting once without a grid id //
      // would order the first grid by every grid's values and leave the rest     //
      // untouched, which is silently wrong rather than visibly broken.           //
      //////////////////////////////////////////////////////////////////////////////
      let sorted = toPublish;
      for (const gridId of toPublish.gridIds)
      {
         if (columnSortRule?.orderBys?.length)
         {
            sorted = sortAxis(sorted, values, columnSortRule.orderBys, "column", gridId);
         }
         if (rowSortRule?.orderBys?.length)
         {
            sorted = sortAxis(sorted, values, rowSortRule.orderBys, "row", gridId);
         }
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // an empty gridHeader list is what keeps a single-grid widget publishing the     //
      // flat columns-and-rows payload it has always published - buildOutputJson only   //
      // emits a grids array once this list has something in it, or a second grid does. //
      ////////////////////////////////////////////////////////////////////////////////////
      ////////////////////////////////////////////////////////////////////////////////////
      // minGrids decides whether a blank first grid is dropped rather than published - //
      // see isGridPublishable.  Passed from the metadata rather than assumed, since it //
      // is the one thing that tells "this widget may have no grids" apart from "this   //
      // widget's one grid happens to be empty".                                        //
      ////////////////////////////////////////////////////////////////////////////////////
      const json = buildOutputJson(sorted, values, allFieldNames, minGrids);

      if (lastPublishedJson.current === json)
      {
         return;
      }
      lastPublishedJson.current = json;

      onSaveCallback?.({[outputFieldName]: json});
   }, [outputFieldName, isEditable, hasSeeded, columnSortRule, rowSortRule, allFieldNames, numericFieldNames, minGrids, onSaveCallback, unmetPrerequisites.length]);


   //////////////////////////////////////////////////////////////////////////////////////
   // publish whenever the structure changes.  This is an effect rather than a call    //
   // inside the setStructure updater: updaters run during the render phase, and       //
   // publishing calls the parent form's setFieldValue - a state update on another     //
   // component mid-render, which React logs about and StrictMode double-invokes.      //
   //                                                                                  //
   // It also covers the first publish after seeding, so that no longer depends on the //
   // bridge re-firing off a values change that publishing itself caused.              //
   //////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      publish(structure);
   }, [structure, publish]);


   /////////////////////////////////////////////////////////////////////////////////
   // re-publish whenever a value changes.  Note this is one setFieldValue on the //
   // parent form per keystroke, which is the cost the render-cost checkpoint is  //
   // meant to measure.  If it proves to be the bottleneck, publishing on blur    //
   // plus on structural change is the cheaper alternative.                       //
   /////////////////////////////////////////////////////////////////////////////////
   const onValuesChanged = useCallback(() =>
   {
      publish(structure);
   }, [publish, structure]);


   ////////////////////////////////////////////////
   // structural mutations - all publish as well //
   ////////////////////////////////////////////////
   /***************************************************************************
    * A caller that just set a value passes it, because valuesRef does not have
    * it yet: it is populated by the Formik bridge on Formik's NEXT render, and
    * a select commits in the same tick as its own setValue.  Reading the ref
    * alone left every alert judged from committed values one change behind -
    * an overlap appeared when the user resolved it, and not when they caused
    * it.  A blur-driven commit is unaffected, which is why text inputs never
    * showed this, and why they pass their value too rather than relying on
    * having been slow enough.
    ***************************************************************************/
   const handleCommit = useCallback((name?: string, value?: any) =>
   {
      /////////////////////////////////////////////////////////////////////////////////
      // trimmed the same way publish trims, so the alerts and the payload agree     //
      // about which cells hold content.  Without this a cell holding one typed      //
      // space, submitted without blurring, was reported missing while publishing as //
      // a real sub-row.  committedValues is only ever read to build the alerts and  //
      // the missing-value list, never fed back into an input, so trimming here      //
      // cannot fight what the user is still typing.                                 //
      /////////////////////////////////////////////////////////////////////////////////
      const raw = name ? {...valuesRef.current, [name]: value} : {...valuesRef.current};
      setCommittedValues(trimStringValues(raw, generatedTrimmableNamesRef.current));
   }, []);

   /***************************************************************************
    * the user pressed Save.
    *
    * Snapshot the values (clicking Save need not have blurred anything), start
    * reporting blank required values, and - if there are any - bring the alerts
    * saying so into view.  block "nearest" so an already-visible alert does not
    * move the page at all, and a scroll the record screen may have done for one
    * of its own errored fields is only overridden as far as it has to be.
    ***************************************************************************/
   const handleSubmitAttempted = useCallback(() =>
   {
      setSubmitAttempted(true);
      handleCommit();

      setTimeout(() =>
      {
         if (missingCountRef.current > 0)
         {
            alertsRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
         }
      }, 250);
   }, [handleCommit]);

   const applyStructureChange = useCallback((change: (current: GridBuilderStructure) => GridBuilderStructure) =>
   {
      //////////////////////////////////////////////////////////////////////////////
      // no publish here - the effect keyed on [structure] above does it, off the //
      // render phase.  Same for dropping removed names, in the effect below.     //
      //////////////////////////////////////////////////////////////////////////////
      setStructure(current => change(current));

      ///////////////////////////////////////////////////////////////////////////////
      // adding or removing a column, row or sub-row is itself a commit - the axis //
      // just changed shape, so any alert about it should be re-judged now         //
      ///////////////////////////////////////////////////////////////////////////////
      handleCommit();
   }, [handleCommit]);

   const handleAddColumn    = useCallback((gridId: number) => applyStructureChange(current => addColumn(current, initialSubRowsPerCell, gridId)), [applyStructureChange, initialSubRowsPerCell]);
   const handleRemoveColumn = useCallback((columnId: number, gridId: number) => applyStructureChange(current => removeColumn(current, columnId, gridId)), [applyStructureChange]);
   const handleAddRow       = useCallback((gridId: number) => applyStructureChange(current => addRow(current, initialSubRowsPerCell, gridId)), [applyStructureChange, initialSubRowsPerCell]);
   const handleRemoveRow    = useCallback((rowId: number, gridId: number) => applyStructureChange(current => removeRow(current, rowId, gridId)), [applyStructureChange]);

   ////////////////////////////////////////////////////////////////////////////////////
   // a new grid opens at its own initial counts, for the same reason a new widget   //
   // does: a grid with no columns or rows in it would greet the user with "at least //
   // 1 Temp. Band is required" before they had done anything to it.                 //
   ////////////////////////////////////////////////////////////////////////////////////
   const handleAddGrid    = useCallback(() => applyStructureChange(current => addGrid(current, initialColumns, initialRows, initialSubRowsPerCell)), [applyStructureChange, initialColumns, initialRows, initialSubRowsPerCell]);

   const handleRemoveGrid = useCallback((gridId: number) => applyStructureChange(current => removeGrid(current, gridId)), [applyStructureChange]);

   /////////////////////////////////////////////////////////////////////////////
   // these two are passed into memoized cells, so they must be stable or the //
   // memoization is defeated by a fresh function identity on every render    //
   /////////////////////////////////////////////////////////////////////////////
   const handleAddSubRow = useCallback((rowId: number, columnId: number, gridId: number) =>
      applyStructureChange(current => addSubRow(current, rowId, columnId, gridId)), [applyStructureChange]);

   const handleRemoveSubRow = useCallback((rowId: number, columnId: number, subRowId: number, gridId: number) =>
   {
      for (const field of cellFields)
      {
         setFieldValueRef.current?.(cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName), null, false);
      }
      applyStructureChange(current => removeSubRow(current, rowId, columnId, subRowId, gridId));
   }, [applyStructureChange, cellFields, widgetName]);


   /***************************************************************************
    * the validation for one cell field.
    *
    * A required cell field cannot use a plain Yup required(): a grid starts every
    * new cell with an empty sub-row so it can be filled in without clicking into
    * it first, so in a grid the user only partly fills, most cells are empty.
    * Requiring each of their fields would mean every untouched cell blocks the
    * save, and the only way to save would be to click the remove control on each
    * one.  An entirely-blank sub-row is instead "nothing here", and only a
    * PARTLY filled one is incomplete.
    *
    * The values of the other fields in the same sub-row are reached through Yup's
    * parent, which for a flat object shape is the whole form values object.
    ***************************************************************************/
   const cellFieldValidation = useCallback((field: QFieldMetaData, rowId: number, columnId: number, subRowId: number, gridId: number): Yup.Schema =>
   {
      ////////////////////////////////////////////////////////////////////////////////
      // let DynamicFormUtils decide whether this field is required at all, so that //
      // decision is not restated here - only the shape of the check changes        //
      ////////////////////////////////////////////////////////////////////////////////
      if (!DynamicFormUtils.getValidationForField(field))
      {
         return (null);
      }

      const cellFieldNames = cellFields.map(cellField => cellField.name);

      return (Yup.mixed().test(
         "gridBuilderCellRequired",
         `${field.label || field.name} is required.`,
         function(value: any)
         {
            ///////////////////////////////////////////////////////////////////////////////
            // trimmed, because publish, the seed, handleCommit and the unique-key test  //
            // all judge trimmed values - and this test has to agree with them or a save //
            // blocks with nothing to act on.  A cell holding one space in an optional   //
            // field is a REAL sub-row to an untrimmed isSubRowBlank, so a required      //
            // sibling is reported missing; but the alert list is built from trimmed     //
            // values and names nothing, and an untouched field draws no marker either.  //
            // Dead Save button, no explanation, and no visible character to delete.     //
            //                                                                           //
            // the value under test is trimmed for the same reason, in the other         //
            // direction: " " is not blank to isBlank, so a required field holding only  //
            // whitespace would pass here and then publish as empty.                     //
            ///////////////////////////////////////////////////////////////////////////////
            const allValues    = trimStringValues((this.parent ?? {}) as Record<string, any>, generatedTrimmableNamesRef.current);
            const trimmedValue = typeof value === "string" ? value.trim() : value;

            if (isSubRowBlank(allValues, cellFieldNames, rowId, columnId, subRowId, gridId, widgetName))
            {
               return (true);
            }
            return (!isBlank(trimmedValue));
         }));
   }, [cellFields]);


   /***************************************************************************
    * the validation for one grid, column or row header field.
    *
    * A plain Yup required() everywhere would block the save on a grid the save
    * is about to drop: an added grid left entirely blank is left out of the
    * published payload and deliberately not reported as missing values, so
    * requiring its headers blocks Save with nothing on screen to explain it -
    * the user sees a dead button.  A grid that MAY be dropped is therefore
    * required only once something is in it, on exactly the condition the payload
    * uses, so the two cannot disagree about whether the grid exists.
    *
    * A grid that may not be dropped is validated exactly as it was before: same
    * schema, from the same call.  That is every grid of a widget whose minGrids
    * makes its first grid non-droppable, and the single anonymous grid of a
    * widget that does not use the grids dimension at all - whose blank required
    * headers are still the user's to fill in.
    ***************************************************************************/
   const headerFieldValidation = useCallback((field: QFieldMetaData, gridId: number): Yup.Schema =>
   {
      const plainValidation = DynamicFormUtils.getValidationForField(field);

      //////////////////////////////////////////////////////////////////////////////////
      // null for a field that is not required at all - DynamicFormUtils decides that //
      // here as it always has, and this only changes the SHAPE of a required check,  //
      // for the grids that need a different one                                      //
      //////////////////////////////////////////////////////////////////////////////////
      if (!plainValidation || !isGridPrunable(structure, gridId, allFieldNames, minGrids))
      {
         return (plainValidation);
      }

      return (Yup.mixed().test(
         "gridBuilderPrunableGridHeaderRequired",
         `${field.label || field.name} is required.`,
         function(value: any)
         {
            ////////////////////////////////////////////////////////////////////////////////
            // the whole grid's values are reached through Yup's parent, which for a flat //
            // object shape is the whole form values object                               //
            ////////////////////////////////////////////////////////////////////////////////
            ////////////////////////////////////////////////////////////////////////////
            // trimmed for the same reason as the cell test above - a grid whose only //
            // content is a space in one header is prunable, and judging it otherwise //
            // blocks the save with an alert list that reports nothing                //
            ////////////////////////////////////////////////////////////////////////////
            const allValues    = trimStringValues((this.parent ?? {}) as Record<string, any>, generatedTrimmableNamesRef.current);
            const trimmedValue = typeof value === "string" ? value.trim() : value;

            if (isGridBlank(structure, allValues, gridId, allFieldNames))
            {
               return (true);
            }
            return (!isBlank(trimmedValue));
         }));
   }, [structure, allFieldNames, minGrids]);


   ////////////////////////////////////////////////////////////////////////////
   // register Yup validations for every input that currently exists, so the //
   // record screen blocks save on a required field left blank               //
   ////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      const formValidations: Record<string, Yup.Schema> = {};

      ////////////////////////////////////////////////////////////////////////////////////
      // with a prerequisite unmet, the whole table is replaced by prerequisiteMessage  //
      // - so none of these inputs are on screen, and the seeding effect has fabricated //
      // blank initial columns and rows behind it.  Registering their validations would //
      // block Save with no errored field visible, and the widget's own alerts cannot   //
      // explain it either: the alerts Box lives in the other branch, so alertsRef is   //
      // null and handleSubmitAttempted's scrollIntoView is a no-op.  Register an empty //
      // group instead, which also clears whatever was registered before.               //
      ////////////////////////////////////////////////////////////////////////////////////
      if (unmetPrerequisites.length > 0)
      {
         addSubValidations?.(widgetMetaData.name, formValidations);
         return;
      }

      for (const gridId of structure.gridIds)
      {
         const axes = getGridAxes(structure, gridId);

         for (const field of gridHeaderFields)
         {
            formValidations[gridFieldName(field.name, gridId, widgetName)] = headerFieldValidation(field, gridId);
         }

         for (const columnId of axes.columnIds)
         {
            for (const field of columnHeaderFields)
            {
               formValidations[columnFieldName(field.name, columnId, gridId, widgetName)] = headerFieldValidation(field, gridId);
            }
         }

         for (const rowId of axes.rowIds)
         {
            for (const field of rowHeaderFields)
            {
               formValidations[rowFieldName(field.name, rowId, gridId, widgetName)] = headerFieldValidation(field, gridId);
            }
            for (const columnId of axes.columnIds)
            {
               for (const subRowId of axes.subRowIds[cellKey(rowId, columnId)] ?? [])
               {
                  for (const field of cellFields)
                  {
                     formValidations[cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)] = cellFieldValidation(field, rowId, columnId, subRowId, gridId);
                  }
               }
            }
         }
      }

      /////////////////////////////////////////////////////////////////////////////////////
      // and one validation standing for the grid as a whole, so a grid with too few     //
      // columns or rows is blocked rather than only complained about.  It hangs off the //
      // output field because that is the one name in the form that represents the whole //
      // grid; the message is not shown anywhere, since the alerts already say it in the //
      // widget's own labels.                                                            //
      /////////////////////////////////////////////////////////////////////////////////////
      /////////////////////////////////////////////////////////////////////////////////////
      // gated on hasSeeded for the same reason the count alerts are: before the grid is //
      // populated its structure is empty, which is indistinguishable from a grid that   //
      // breaks its own count rules.                                                     //
      /////////////////////////////////////////////////////////////////////////////////////
      /////////////////////////////////////////////////////////////////////////////////////
      // judged per grid, and gated on the same condition as the alerts that explain it. //
      // A blocking validation the alerts stay quiet about is a save the user cannot     //
      // make and cannot be told why - which is the failure the unmet-prerequisite note  //
      // above describes.                                                                //
      /////////////////////////////////////////////////////////////////////////////////////
      const anyGridIsShort = structure.gridIds.some(gridId =>
      {
         const axes = getGridAxes(structure, gridId);
         return (axes.columnIds.length < minColumns || axes.rowIds.length < minRows);
      });
      const countsAreShort = anyGridIsShort || (usesGrids && structure.gridIds.length < minGrids);

      /////////////////////////////////////////////////////////////////////////////////////
      // Overlapping grid headers are judged HERE rather than at registration time, and  //
      // read from the values Yup hands the test.  The counts above depend only on the   //
      // structure, so this effect re-registers whenever they change - but an overlap    //
      // depends on the values, which this effect does not watch, so a registration-time //
      // check would go stale the moment a user picked the duplicate.  That is how the   //
      // overlap came to be reported on screen and still let the form submit, leaving    //
      // the database's unique key to catch what the widget had already spotted.         //
      /////////////////////////////////////////////////////////////////////////////////////
      //////////////////////////////////////////////////////////////////////////////////////
      // hung off a name of the WIDGET's own, and not off outputFieldName.  A widget      //
      // group is layered over the table's own validations by field name, so registering  //
      // under a real table field would REPLACE whatever the table declared for it -      //
      // silently dropping, say, a required rule on the output field.  Nothing renders    //
      // this message either way (the widget's alerts already say it in the user's own    //
      // terms), so the key only has to be unique, and Yup runs a shape's test whether or //
      // not the values object has that key.                                              //
      //////////////////////////////////////////////////////////////////////////////////////
      if (outputFieldName && isEditable && hasSeeded)
      {
         const gridHeaderFieldNames = gridHeaderFields.map(field => field.name);

         formValidations[`__gridBuilder_${widgetMetaData.name}_isComplete`] = Yup.mixed().test("gridBuilder", "The grid is incomplete.", function ()
         {
            if (countsAreShort)
            {
               return (false);
            }

            if (usesGrids && findGridHeaderOverlaps(structure, this.parent ?? {}, gridHeaderFieldNames).length > 0)
            {
               return (false);
            }

            ////////////////////////////////////////////////////////////////////////////////////
            // the unique keys block the save for the same reason the overlap above does, and //
            // read the values the same way.  Reported on screen but not blocking was exactly //
            // the overlap's old behavior: the alert named the duplicate, the save went       //
            // through anyway, and the database's unique key caught what the widget had       //
            // already spotted.  The alerts at buildAlerts() word these for the user; this    //
            // only has to agree with them about whether the form may be submitted.           //
            //                                                                                //
            // the key lists are deliberately NOT in this effect's dependency array.  They    //
            // come from readList, which hands back a fresh [] for a widget declaring none -  //
            // a new identity on every render, which would re-run this effect, re-register    //
            // the group, re-render, and loop.  They are constant for the widget's lifetime,  //
            // so the closure over them is sound, the way widgetMetaData.name already is.     //
            ////////////////////////////////////////////////////////////////////////////////////
            ////////////////////////////////////////////////////////////////////////////////////
            // trimmed, because the alert that explains a violation and the payload that      //
            // carries it are both trimmed, and all three have to agree about what a value    //
            // IS.  Untrimmed here, both asymmetries bite: "ABC" beside "ABC " is a duplicate //
            // to the alert and to the backend but not to this test, so the alert appears and //
            // the save still goes through - the very thing folding these checks in was meant //
            // to stop - while two whitespace-only values are a duplicate to this test and to //
            // nothing else, blocking the save with no alert and no errored input to explain  //
            // it.  Trailing whitespace survives a backend load, so neither needs a typo to   //
            // reach: nothing trims a value the user never focused.                           //
            ////////////////////////////////////////////////////////////////////////////////////
            const values = trimStringValues((this.parent ?? {}) as Record<string, any>, generatedTrimmableNamesRef.current);
            const violations = [
               ...findUniqueKeyViolations(structure, values, columnHeaderUniqueKeys, "column"),
               ...findUniqueKeyViolations(structure, values, rowHeaderUniqueKeys, "row"),
               ...findUniqueKeyViolations(structure, values, cellUniqueKeys, "cell"),
            ];

            return (violations.length === 0);
         });
      }

      addSubValidations?.(widgetMetaData.name, formValidations);
   }, [structure, gridHeaderFields, columnHeaderFields, rowHeaderFields, cellFields, cellFieldValidation, headerFieldValidation, outputFieldName, isEditable, hasSeeded, usesGrids, minGrids, minColumns, minRows, unmetPrerequisites.length]);


   //////////////////////////////////////////////////////////////////////////////////
   // give the group back when this widget goes away - an adjuster that hides this //
   // widget's section unmounts it, and a group left behind validates fields with  //
   // no rendered input, which blocks Save with nothing on screen to explain it.   //
   //////////////////////////////////////////////////////////////////////////////////
   useEffect(() => () => addSubValidations?.(widgetMetaData.name, {}), []);


   /***************************************************************************
    * the required fields in the grid that are currently blank, if the user is
    * at a point where they should be told about them
    ***************************************************************************/
   function missingRequiredValues(): MissingRequiredValue[]
   {
      ////////////////////////////////////////////////////////////////////////////////
      // committed values, not live ones - a value being typed is not missing.  And //
      // nothing at all until the user has tried to save: see the Formik bridge.    //
      ////////////////////////////////////////////////////////////////////////////////
      if (!isEditable || !submitAttempted)
      {
         return ([]);
      }

      //////////////////////////////////////////////////////////////////////////////////
      // the model decides for itself whether to name the grid in each location, from //
      // the same predicate usesGrids is read from - so a location never names a grid //
      // the user has no control over.                                                //
      //////////////////////////////////////////////////////////////////////////////////
      return (findMissingRequiredValues(
         structure,
         committedValues,
         {gridHeader: gridHeaderFields, columnHeader: columnHeaderFields, rowHeader: rowHeaderFields, cell: cellFields},
         {grid: gridLabel, column: columnLabel, row: rowLabel, subRow: subRowLabel},
         minGrids,
         resolveValueLabel));
   }


   /***************************************************************************
    * alerts shown between the label and the table
    ***************************************************************************/
   function buildAlerts(missingValues: MissingRequiredValue[]): JSX.Element[]
   {
      const alerts: JSX.Element[] = [];

      ///////////////////////////////////////////////////////////////////////////////////
      // deliberately the committed values, not valuesRef.current - see the comment on //
      // committedValues.  a keystroke must not make these alerts appear or vanish.    //
      ///////////////////////////////////////////////////////////////////////////////////
      const values = committedValues;

      ///////////////////////////////////////////////////////////////////////////////////
      // blank required values, first: this is the one alert that is blocking the save //
      // the user just asked for, so it is what they need to read.                     //
      ///////////////////////////////////////////////////////////////////////////////////
      if (missingValues.length > 0)
      {
         /////////////////////////////////////////////////////////////////////////////
         // cap the list - a grid saved with nothing filled in would otherwise list //
         // every cell in it, which is a wall of text rather than a place to start  //
         /////////////////////////////////////////////////////////////////////////////
         const shownValues   = missingValues.slice(0, MAX_LISTED_MISSING_VALUES);
         const notShownCount = missingValues.length - shownValues.length;

         alerts.push(
            <GridBuilderAlert key="missing-values">
               <Alert severity="error">
                  {missingValues.length === 1 ? "This value is required:" : `These ${missingValues.length} values are required:`}
                  <Box component="ul" sx={{margin: 0, paddingLeft: "1.25rem"}}>
                     {shownValues.map((missingValue, index) => (
                        <Box component="li" key={index}>{`${missingValue.location}: ${missingValue.fieldLabel}`}</Box>
                     ))}
                     {notShownCount > 0 && (
                        <Box component="li">{notShownCount === 1 ? "and 1 more" : `and ${notShownCount} more`}</Box>
                     )}
                  </Box>
               </Alert>
            </GridBuilderAlert>
         );
      }

      //////////////////////////////////////////////////////////////////////////
      // out-of-order axes.  the widget never reorders on its own - this just //
      // says what saving will do, and optionally offers to do it now         //
      //////////////////////////////////////////////////////////////////////////
      const sortRules: { rule: GridBuilderSortRule, axis: GridBuilderAxis }[] = [
         {rule: columnSortRule, axis: "column"},
         {rule: rowSortRule, axis: "row"},
      ];

      for (const {rule, axis} of sortRules)
      {
         if (!isEditable || !rule?.doAlert || !rule?.orderBys?.length)
         {
            continue;
         }
         //////////////////////////////////////////////////////////////////////////////////
         // one alert for the axis across every grid, rather than one per grid: the rule //
         // is a property of the axis, so repeating the same sentence per grid would say //
         // nothing new.  Sorting then puts every grid in order, since the alert stands  //
         // for all of them.                                                             //
         //////////////////////////////////////////////////////////////////////////////////
         if (structure.gridIds.every(gridId => isAxisSorted(structure, values, rule.orderBys, axis, gridId)))
         {
            continue;
         }

         alerts.push(
            <GridBuilderAlert key={`sort-${axis}`}>
               <Alert
                  severity={(rule.alertType ?? "INFO").toLowerCase() as any}
                  action={rule.offerSortAction
                     ? <Button onClick={() => applyStructureChange(current =>
                     {
                        let result = current;
                        for (const gridId of current.gridIds)
                        {
                           result = sortAxis(result, valuesRef.current, rule.orderBys, axis, gridId);
                        }
                        return (result);
                     })}>
                        {rule.sortActionLabel ?? "Sort now"}
                     </Button>
                     : undefined}
               >
                  {rule.alertMessage}
               </Alert>
            </GridBuilderAlert>
         );
      }

      ///////////////////////////////////////
      // duplicate values on any of 3 axes //
      ///////////////////////////////////////
      const violations: UniqueKeyViolation[] = [
         ...findUniqueKeyViolations(structure, values, columnHeaderUniqueKeys, "column"),
         ...findUniqueKeyViolations(structure, values, rowHeaderUniqueKeys, "row"),
         ...findUniqueKeyViolations(structure, values, cellUniqueKeys, "cell"),
      ];

      violations.forEach((violation, index) =>
      {
         const label = violation.label ?? violation.fieldNames.map(fieldName => labelForField(violation.axis, fieldName)).join(" + ");
         const scope = violation.axis === "column" ? "columns" : violation.axis === "row" ? "rows" : "items in one cell";

         ////////////////////////////////////////////////////////////////////////////////////
         // say WHICH grid the duplicate is in, since the model scopes violations per grid //
         // and there may be several tables on screen.  Only when grids are a dimension    //
         // the user can see: with one anonymous grid there is nothing to disambiguate,    //
         // and naming it would both be noise and change a message nothing asked to        //
         // change.                                                                        //
         ////////////////////////////////////////////////////////////////////////////////////
         const gridPrefix = usesGrids ? `${describeGrid(violation.gridId ?? DEFAULT_GRID_ID)}: ` : "";

         alerts.push(
            <GridBuilderAlert key={`unique-${index}`}>
               <Alert severity="error">{`${gridPrefix}Two ${scope} have the same ${label}.  Each must be different.`}</Alert>
            </GridBuilderAlert>
         );
      });

      ///////////////////////////////////////////////////////////////////////////////////
      // one value claimed by two grids - a carton type covered by two override grids. //
      // Not expressible as a unique key; see findGridHeaderOverlaps.                  //
      //                                                                               //
      // Only computed when grids are a dimension the user can see and act on, so a    //
      // widget that has not opted in is untouched by the rule rather than merely      //
      // producing nothing from it.                                                    //
      ///////////////////////////////////////////////////////////////////////////////////
      const overlaps: GridHeaderOverlap[] = usesGrids
         ? findGridHeaderOverlaps(structure, values, gridHeaderFields.map(field => field.name))
         : [];

      overlaps.forEach((overlap, index) =>
      {
         const fieldLabel = labelForGridHeaderField(overlap.fieldName);

         ////////////////////////////////////////////////////////////////////////////////////
         // the grids are deliberately NOT named.  A grid is named by the header values it //
         // holds, so when the value being reported is the only one a grid holds, naming   //
         // both grids says the same thing three times over.  The value is what the user   //
         // has to act on; which grids hold it, they can see on screen.                    //
         ////////////////////////////////////////////////////////////////////////////////////
         alerts.push(
            <GridBuilderAlert key={`grid-overlap-${index}`}>
               <Alert severity="error">{`${fieldLabel} ${describeGridHeaderValue(overlap)} appears in more than one ${gridLabel}.  It may only be in one.`}</Alert>
            </GridBuilderAlert>
         );
      });

      //////////////////
      // count bounds //
      //////////////////
      //////////////////////////////////////////////////////////////////////////////////
      // count shortfalls, named with the configured labels.  with initialColumns and //
      // initialRows defaulting to the minimums, and the remove control hidden at the //
      // minimum, these are hard to reach - they are a backstop, not the normal path. //
      //////////////////////////////////////////////////////////////////////////////////
      if (isEditable && hasSeeded && usesGrids && structure.gridIds.length < minGrids)
      {
         alerts.push(
            <GridBuilderAlert key="minGrids">
               <Alert severity="error">{countShortfallMessage(minGrids, gridLabel)}</Alert>
            </GridBuilderAlert>
         );
      }

      /////////////////////////////////////////////////////////////////////////////////////
      // one alert per axis however many grids fall short of it, for the same reason the //
      // sort alerts are not repeated: the minimum belongs to the axis, so naming every  //
      // grid that breaks it would restate one rule several times.                       //
      /////////////////////////////////////////////////////////////////////////////////////
      const gridsShortOfColumns = structure.gridIds.some(gridId => getGridAxes(structure, gridId).columnIds.length < minColumns);
      const gridsShortOfRows    = structure.gridIds.some(gridId => getGridAxes(structure, gridId).rowIds.length < minRows);

      if (isEditable && hasSeeded && gridsShortOfColumns)
      {
         alerts.push(
            <GridBuilderAlert key="minColumns">
               <Alert severity="error">{countShortfallMessage(minColumns, columnLabel)}</Alert>
            </GridBuilderAlert>
         );
      }
      if (isEditable && hasSeeded && gridsShortOfRows)
      {
         alerts.push(
            <GridBuilderAlert key="minRows">
               <Alert severity="error">{countShortfallMessage(minRows, rowLabel)}</Alert>
            </GridBuilderAlert>
         );
      }

      return (alerts);
   }


   /***************************************************************************
    * how to refer to one grid in a message - the values the user chose in its
    * header, falling back to its position when they are blank (which is often
    * the case precisely because a blank header IS the problem being reported).
    *
    * Mirrors the rule gridBuilderModel uses for a missing value's location, so
    * the two places a grid gets named agree.  Restated here because that module
    * keeps its own descriptor private - and named through the same resolver that
    * module is handed, so both name a grid by its labels rather than its ids.
    ***************************************************************************/
   function describeGrid(gridId: number): string
   {
      const headerValues = gridHeaderFields
         .map(field => ({fieldName: field.name, inputName: gridFieldName(field.name, gridId, widgetName)}))
         .filter(entry => !isBlank(committedValues[entry.inputName]))
         .map(entry => resolveValueLabel(entry.fieldName, entry.inputName, committedValues[entry.inputName]));

      if (headerValues.length > 0)
      {
         return (`${gridLabel} ${headerValues.join(" / ")}`);
      }

      return (`${gridLabel} #${structure.gridIds.indexOf(gridId) + 1}`);
   }


   /***************************************************************************
    * how to name one value held by one input in a message: as the label the
    * user picked, not as the possible-value id the grid holds.
    *
    * A multi-value input holds several ids in its one value, so it is named by
    * its several labels, joined the way view mode joins them.  An id nothing
    * has a label for is named as itself, so a partly-resolved set says what it
    * knows instead of hiding it.
    *
    * A value that is not an id at all - a typed temperature - resolves to
    * nothing anywhere and comes back as itself, which is what keeps every
    * message about a plain grid exactly what it was.
    ***************************************************************************/
   function resolveValueLabel(fieldName: string, inputName: string, value: any): string
   {
      const holdsSeveralIds = multipleValueGridHeaderFields.some(field => field.name === fieldName);
      const ids             = holdsSeveralIds ? splitMultipleValue(value) : [value];
      const labels          = ids.map(id => resolveIdLabel(fieldName, [inputName], id));

      if (labels.every(label => isBlank(label)))
      {
         ///////////////////////////////////////////////////////////////////////////////
         // with no id resolved at all, the label for the value AS A WHOLE: the one   //
         // that came back with a pick made in this session, then the one the backend //
         // sent - which for a multi-value header is its several labels already       //
         // joined.  The pick comes first because it is the newer of the two: the     //
         // backend's display value describes the value as it was loaded, which a     //
         // pick has since replaced.  And with neither, the value itself.             //
         ///////////////////////////////////////////////////////////////////////////////
         return (pickedDisplayValues[inputName] ?? displayValues[inputName] ?? String(value));
      }

      return (ids.map((id, index) => (isBlank(labels[index]) ? String(id) : labels[index])).join(", "));
   }


   /***************************************************************************
    * the label ONE possible-value id resolves to, or nothing when none of the
    * labels the widget holds covers it.
    *
    * The labels are the ones already resolved for the inputs themselves: by
    * field for the ids the backend sent, and by input for an id picked in this
    * session, which is why the inputs that could have picked it are passed in.
    ***************************************************************************/
   function resolveIdLabel(fieldName: string, inputNames: string[], id: any): string
   {
      return (valueLabels[fieldName]?.[id]
         ?? inputNames
            .map(inputName => pickedValueLabels[inputName]?.[id])
            .find(pickedLabel => !isBlank(pickedLabel)));
   }


   /***************************************************************************
    * label for a field on one of the three axes, for use in messages
    ***************************************************************************/
   function labelForField(axis: GridBuilderAxis, fieldName: string): string
   {
      const fields = axis === "column" ? columnHeaderFields : axis === "row" ? rowHeaderFields : cellFields;
      return (fields.find(field => field.name === fieldName)?.label ?? fieldName);
   }


   /***************************************************************************
    * label for a grid header field, for use in messages.  A grid header is not
    * one of the three axes above - GridBuilderAxis has no grid member - so
    * labelForField cannot answer for it.
    *
    * The model's fallback covers a field carrying no label, so a message about a
    * grid header calls the field what a message about a missing value calls it.
    ***************************************************************************/
   function labelForGridHeaderField(fieldName: string): string
   {
      const field = gridHeaderFields.find(gridHeaderField => gridHeaderField.name === fieldName);
      return (field ? fieldDisplayLabel(field) : fieldName);
   }


   /***************************************************************************
    * how to name one grid header value in a message.
    *
    * A grid header holds possible-value ids, so the value two grids share is an
    * id - and "Carton Type 4" is not what the user is looking at.  An id nothing
    * has a label for is named as itself rather than not at all.
    ***************************************************************************/
   function describeGridHeaderValue(overlap: GridHeaderOverlap): string
   {
      //////////////////////////////////////////////////////////////////////////////
      // ONE id, so resolved as one - deliberately not through resolveValueLabel, //
      // whose whole-value fallbacks describe the SET an input holds and would    //
      // name this single id after every id beside it.                            //
      //////////////////////////////////////////////////////////////////////////////
      const inputNames = overlap.gridIds.map(gridId => gridFieldName(overlap.fieldName, gridId, widgetName));
      const label      = resolveIdLabel(overlap.fieldName, inputNames, overlap.value);

      return (isBlank(label) ? String(overlap.value) : label);
   }


   //////////////////////////////////////////////////////////////////////////////////////
   // the add and remove controls for grids themselves.  Both require usesGrids, so a  //
   // widget that has not opted into the dimension shows neither however its count     //
   // metadata reads - null maxGrids still means unlimited, as it does for columns and //
   // rows, rather than being reinterpreted here to keep the old layout.               //
   //                                                                                  //
   // A consequence worth naming: a widget with no grid header fields can never reach  //
   // a second grid, since adding one needs the predicate and the predicate needs      //
   // either header fields or a second grid.  A grid with nothing to identify it by is //
   // not a configuration worth reaching, so that is by design.                        //
   //////////////////////////////////////////////////////////////////////////////////////
   const mayAddGrid    = isEditable && usesGrids && (maxGrids === null || structure.gridIds.length < maxGrids);
   const mayRemoveGrid = isEditable && usesGrids && structure.gridIds.length > minGrids;

   //////////////////////////////////////////////////////////////////////////////////
   // whether to say "none exist" in place of the grids.  Read-only only: while    //
   // editing, mayAddGrid puts an "+ Add <gridLabel>" control here, so the area    //
   // already explains itself and already has something in it.                     //
   //                                                                              //
   // usesGrids, not a count test alone - a single anonymous grid always holds one //
   // entry in gridIds, so only a multi-grid widget can reach zero.  That is what  //
   // keeps every other consumer of this widget rendering exactly as it did.       //
   //////////////////////////////////////////////////////////////////////////////////
   const showsNoGridsMessage = !isEditable && usesGrids && structure.gridIds.length === 0;

   /////////////////////////////////////////////////////////////////////////////////////
   // a grid gets a section header when there is something to put in it - its own     //
   // header inputs, or the control to remove it.  With neither, no element is        //
   // emitted at all, which is what keeps a single anonymous grid's markup unchanged. //
   /////////////////////////////////////////////////////////////////////////////////////
   const gridSectionHeaderIsShown = usesGrids && (gridHeaderFields.length > 0 || mayRemoveGrid);

   ////////////////////////////////////////////////////////////////////////////////////
   // how far the table reaches back past the widget's own padding, so it sits flush //
   // with the edge of the card it is in - which is what a child-record-list does,   //
   // and is why its header shading reads as a band across the whole card.           //
   //                                                                                //
   // Two values because there are two paddings this widget can be inside of:        //
   // RecordScreenSection overrides a widget card to a uniform 24px (which is the    //
   // padding RecordGridWidget's own -3 margin is cancelling), while everywhere else //
   // - a process screen, a workflow step - gets Widget's own "24px 16px".           //
   ////////////////////////////////////////////////////////////////////////////////////
   const bleedMargin = isForRecordViewAndEditScreen ? "-24px" : "-16px";

   ///////////////////////////////////////////////////////////////////////////////////
   // the header row's shading, also carried by the row-header column and by the    //
   // add-column cell - a header cell holding a control is still a header cell, and //
   // leaving that one unshaded read as a gap in the header row.                    //
   //                                                                               //
   // Set as a cell background rather than a row one, which is also what keeps the  //
   // row hover below off the header cells: a cell's own background paints over the //
   // row's, so the shaded cells stay put while the data cells light up.            //
   ///////////////////////////////////////////////////////////////////////////////////
   const headerBackgroundSX = {
      background: TABLE_HEADER_BACKGROUND,
      whiteSpace: "nowrap",

      ///////////////////////////////////////////////////////////////////////////////
      // header text, which is what the child list themes on                       //
      // .MuiDataGrid-columnHeaderTitle.  The cell's own color is what a view-mode //
      // header VALUE inherits, since GridBuilderInput renders that as bare text.  //
      ///////////////////////////////////////////////////////////////////////////////
      color: `${TABLE_HEADER_TEXT_COLOR} !important`,
      fontWeight: 500,

      /////////////////////////////////////////////////////////////////////////////////////
      // and the LABEL has to be named directly.  GridBuilderFieldLabel declares both a  //
      // color and a weight on the Typography itself, and an inherited value loses to a  //
      // declaration on the element regardless of !important - so the cell's rules above //
      // cannot reach it.  Two classes deep beats the Typography's own one-class emotion //
      // rule.  500 rather than bold is the weight the child list's column headers use.  //
      /////////////////////////////////////////////////////////////////////////////////////
      "& .fieldLabel": {
         color: `${TABLE_HEADER_TEXT_COLOR} !important`,
         fontWeight: "500 !important",
      },

      /////////////////////////////////////////////////////////////////////////////////////
      // but NOT the controls in a header cell.  whiteInputBackgroundSX fills those with //
      // white so they do not read as disabled against the shading, so a theme whose     //
      // header text is light - the ThemeIT fixture uses white on purple - would leave   //
      // a header's own value unreadable inside its own box.  This is the color the app  //
      // gives a DataGrid cell.                                                          //
      /////////////////////////////////////////////////////////////////////////////////////
      "& .MuiInputBase-root": {color: "rgb(45, 45, 45)"},
   };

   const stickyCellSX = {
      position: "sticky",
      left: 0,
      zIndex: 2,
      ...headerBackgroundSX,
   };

   const cellSX = {
      borderTop: `1px solid ${TABLE_BORDER_COLOR}`,
      borderBottom: `1px solid ${TABLE_BORDER_COLOR}`,

      ///////////////////////////////////////////////////////////////////////////////////
      // 10px horizontally is what a DataGrid cell uses, so a column in this table     //
      // lines its contents up the same distance from its edge as one in a child list. //
      // The vertical padding is this table's own: a DataGrid gets its row height from //
      // its density instead, and a fixed 52px row would be mostly empty space around  //
      // a 26px control.                                                               //
      ///////////////////////////////////////////////////////////////////////////////////
      padding: "8px 10px",
      verticalAlign: "top",
      textAlign: "left",

      //////////////////////////////////////////////////////////////////////////////////////
      // on the cell rather than on the row: the buttons shown are the ones acting on     //
      // whatever the pointer is actually over, so moving along a row reveals each cell's //
      // controls in turn instead of lighting up the whole row at once                    //
      //////////////////////////////////////////////////////////////////////////////////////
      ...hoverRevealSX,

      ...whiteInputBackgroundSX,
   };

   ///////////////////////////////////////////////////////////////////////////////////
   // header cells hold a single control, so centering them vertically reads better //
   // than top-aligning against a body row that may be several sub-rows tall        //
   ///////////////////////////////////////////////////////////////////////////////////
   //////////////////////////////////////////////////////////////////////////////////////
   // fontWeight so a header VALUE reads as normal text.  The labels beside it set     //
   // their own weight (Typography variant="button"), so this reaches only the values, //
   // which would otherwise inherit whatever the card around them uses.                //
   //////////////////////////////////////////////////////////////////////////////////////
   const headerCellSX = {...cellSX, verticalAlign: "middle", fontWeight: 400};

   /////////////////////////////////////////////////////////////////////////////////////
   // a body row lights up under the pointer, as a child-record-list row does.  On    //
   // the row and not the cell - which is the opposite of the hover-reveal above, and //
   // deliberately so: the shading says which row you are on, while the buttons say   //
   // which cell you are in.                                                          //
   /////////////////////////////////////////////////////////////////////////////////////
   const bodyRowSX = {
      "&:hover": {backgroundColor: TABLE_ROW_HOVER_COLOR},
   };

   /***************************************************************************
    * one grid - its header inputs, and its table of columns, rows and cells.
    *
    * Returns a Fragment rather than a wrapper element, so a widget with one
    * anonymous grid emits exactly the DOM it emitted before grids existed: the
    * Fragment contributes nothing, the section header below is suppressed, and
    * what is left is the table wrapper that used to sit here inline.  A separate
    * legacy branch would have had to be kept in step with this one by hand; a
    * loop that degenerates cannot drift.
    ***************************************************************************/
   function renderGrid(gridId: number, gridIndex: number): JSX.Element
   {
      const axes = getGridAxes(structure, gridId);

      /////////////////////////////////////////////////////////////////////////////////
      // per grid, from that grid's own counts - a grid at its column limit must not //
      // suppress the add control on a grid that is not                              //
      /////////////////////////////////////////////////////////////////////////////////
      const mayAddColumn = isEditable && (maxColumns === null || axes.columnIds.length < maxColumns);
      const mayAddRow    = isEditable && (maxRows === null || axes.rowIds.length < maxRows);

      /////////////////////////////////////////////////////////////////////////////////////
      // grids after the first need separating from the one above.  When this grid has a //
      // section header that gap belongs on the header; with no header it goes on the    //
      // table.  Spread conditionally so the first grid's object is exactly the literal  //
      // that was here before - emotion keys its generated class on the style set, so an //
      // added-then-zeroed property would still be a different class.                    //
      /////////////////////////////////////////////////////////////////////////////////////
      const tableNeedsTopGap = gridIndex > 0 && !gridSectionHeaderIsShown;

      ///////////////////////////////////////////////////////////////////////////////////
      // whether this table's bottom edge is the card's bottom edge, which is the only //
      // reason it rounds its bottom corners at all.  Anything below it - a following  //
      // grid, or the add-grid control - makes it an interior edge, and an interior    //
      // edge that curves reads as the end of the card when it is not.                 //
      ///////////////////////////////////////////////////////////////////////////////////
      const tableIsCardBottom = gridIndex === structure.gridIds.length - 1 && !mayAddGrid;

      return (
         <React.Fragment key={gridId}>
            {gridSectionHeaderIsShown && (
               //////////////////////////////////////////////////////////////////////////////////
               // the grid's own header inputs, laid out like a row header's - the same label  //
               // and input pairs, so a grid reads as the outermost of the same kind of thing. //
               //                                                                              //
               // The remove control is NOT hover-revealed, unlike the per-column, per-row and //
               // per-cell ones.  Those hide because a grid full of them reads as a field of   //
               // icons; there is only one of these per grid, and hover-revealing it inside an //
               // element that carries no hoverRevealSX would leave it permanently invisible.  //
               //////////////////////////////////////////////////////////////////////////////////
               /////////////////////////////////////////////////////////////////////////////////
               // fontSize here for the same reason the table sets it: a grid header sits     //
               // OUTSIDE the table, so its inputs inherit the widget card's 20px rather than //
               // the grid's own size, and a header value renders half again as large as the  //
               // cells below it.  The labels escape this only because                        //
               // GridBuilderFieldLabel sets its own size.                                    //
               /////////////////////////////////////////////////////////////////////////////////
               <Box display="flex" alignItems="center" gap="4px" whiteSpace="nowrap" mb="0.5rem" mt={gridIndex === 0 ? 0 : "2rem"} fontSize={GRID_FONT_SIZE} lineHeight={1.2} fontWeight={400}>
                  {gridHeaderFields.map(field => (
                     <React.Fragment key={field.name}>
                        <GridBuilderFieldLabel field={field} isEditable={isEditable} />
                        <GridBuilderInput
                           field={field}
                           name={gridFieldName(field.name, gridId, widgetName)}
                           isEditable={isEditable}
                           isMultiple={isMultipleValueField(field)}
                           otherValues={otherValues}
                           displayValue={displayValues[gridFieldName(field.name, gridId, widgetName)]}
                           pickedDisplayValue={pickedDisplayValues[gridFieldName(field.name, gridId, widgetName)]}
                           valueLabels={valueLabels[field.name]}
                           pickedValueLabels={pickedValueLabels[gridFieldName(field.name, gridId, widgetName)]}
                           viewValue={loadedValues[gridFieldName(field.name, gridId, widgetName)]}
                           clearGeneration={clearGeneration}
                           onCommit={handleCommit}
                           onPickDisplayValue={onPickDisplayValue}
                           onPickValueLabels={onPickValueLabels}
                        />
                     </React.Fragment>
                  ))}
                  {mayRemoveGrid && (
                     <Tooltip title={`Remove ${gridLabel}`} enterDelay={500}>
                        <Button sx={xIconButtonSX} onClick={() => handleRemoveGrid(gridId)}><Icon>clear</Icon></Button>
                     </Tooltip>
                  )}
               </Box>
            )}

            {/****************************************************************************
              * the bottom two corners, and only when this table's bottom edge is the
              * card's bottom edge (the card's own padding-bottom is removed below), so it
              * is rounded off the same way the card is.  Its top edge meets the widget's
              * label rather than the card, and wants no corner at all.
              *
              * A grid with another grid below it, or with the add-grid control below it,
              * has an interior bottom edge - and an interior edge that curves reads as
              * the end of the card when it is not.
              *
              * Rounded HERE and scrolled one level in, rather than both on one element.
              * A horizontal scrollbar is not content - it is drawn by the browser in the
              * scroll container's own gutter, which the container's border-radius does
              * not clip - so a rounded scroller lays a square-cornered scrollbar across
              * the exact strip that was supposed to be curved.  Clipping from the outside
              * cuts the scrollbar's ends to the curve instead.
              ****************************************************************************/}
            <Box sx={{mx: bleedMargin, overflow: "hidden", ...(tableIsCardBottom ? {borderBottomLeftRadius: "0.75rem", borderBottomRightRadius: "0.75rem"} : {}), ...(tableNeedsTopGap ? {mt: "2rem"} : {})}}>
               <Box sx={{overflowX: "auto"}}>
                  {/****************************************************************************
                    * width 100% is what carries the header shading and the row rules all the
                    * way across the card.  It does NOT stretch the real columns: the spacer
                    * cell at the end of each row claims 100% of the width, so the browser
                    * gives it everything left over and the columns keep their content width.
                    ****************************************************************************/}
                  <Box component="table" sx={{borderCollapse: "collapse", width: "100%", fontSize: GRID_FONT_SIZE, lineHeight: 1.2}}>
                     <Box component="thead">
                        <Box component="tr">
                           <Box component="th" sx={{...headerCellSX, ...stickyCellSX}} />
                           {axes.columnIds.map(columnId => (
                              <Box component="th" key={columnId} sx={{...headerCellSX, ...headerBackgroundSX}}>
                                 <Box display="flex" alignItems="center" gap="4px">
                                    {columnHeaderFields.map(field => (
                                       <React.Fragment key={field.name}>
                                          <GridBuilderFieldLabel field={field} isEditable={isEditable} />
                                          <GridBuilderInput
                                             field={field}
                                             name={columnFieldName(field.name, columnId, gridId, widgetName)}
                                             isEditable={isEditable}
                                             otherValues={otherValues}
                                             displayValue={displayValues[columnFieldName(field.name, columnId, gridId, widgetName)]}
                                             pickedDisplayValue={pickedDisplayValues[columnFieldName(field.name, columnId, gridId, widgetName)]}
                                             viewValue={loadedValues[columnFieldName(field.name, columnId, gridId, widgetName)]}
                                             clearGeneration={clearGeneration}
                                             onCommit={handleCommit}
                                             onPickDisplayValue={onPickDisplayValue}
                                          />
                                       </React.Fragment>
                                    ))}
                                    {isEditable && axes.columnIds.length > minColumns && (
                                       <Tooltip title={`Remove ${columnLabel}`} enterDelay={500}>
                                          <Button className={HOVER_BUTTON_CLASS} sx={xIconButtonSX} onClick={() => handleRemoveColumn(columnId, gridId)}><Icon>clear</Icon></Button>
                                       </Tooltip>
                                    )}
                                 </Box>
                              </Box>
                           ))}
                           {mayAddColumn && (
                              <Box component="th" sx={{...headerCellSX, ...headerBackgroundSX}}>
                                 <Button onClick={() => handleAddColumn(gridId)} sx={addAxisButtonSX}>{`+ Add ${columnLabel}`}</Button>
                              </Box>
                           )}
                           <Box component="th" sx={{...headerCellSX, ...headerBackgroundSX, ...SPACER_CELL_SX}} />
                        </Box>
                     </Box>

                     <Box component="tbody">
                        {axes.rowIds.map(rowId => (
                           <Box component="tr" key={rowId} sx={bodyRowSX}>
                              <Box component="td" sx={{...cellSX, ...stickyCellSX}}>
                                 <Box display="flex" alignItems="center" gap="4px">
                                    {rowHeaderFields.map(field => (
                                       <React.Fragment key={field.name}>
                                          <GridBuilderFieldLabel field={field} isEditable={isEditable} />
                                          <GridBuilderInput
                                             field={field}
                                             name={rowFieldName(field.name, rowId, gridId, widgetName)}
                                             isEditable={isEditable}
                                             otherValues={otherValues}
                                             displayValue={displayValues[rowFieldName(field.name, rowId, gridId, widgetName)]}
                                             pickedDisplayValue={pickedDisplayValues[rowFieldName(field.name, rowId, gridId, widgetName)]}
                                             viewValue={loadedValues[rowFieldName(field.name, rowId, gridId, widgetName)]}
                                             clearGeneration={clearGeneration}
                                             onCommit={handleCommit}
                                             onPickDisplayValue={onPickDisplayValue}
                                          />
                                       </React.Fragment>
                                    ))}
                                    {isEditable && axes.rowIds.length > minRows && (
                                       <Tooltip title={`Remove ${rowLabel}`} enterDelay={500}>
                                          <Button className={HOVER_BUTTON_CLASS} sx={xIconButtonSX} onClick={() => handleRemoveRow(rowId, gridId)}><Icon>clear</Icon></Button>
                                       </Tooltip>
                                    )}
                                 </Box>
                              </Box>
                              {axes.columnIds.map(columnId => (
                                 <Box component="td" key={columnId} sx={cellSX}>
                                    <GridBuilderCellComponent
                                       widgetName={widgetName}
                                       gridId={gridId}
                                       rowId={rowId}
                                       columnId={columnId}
                                       subRowIds={axes.subRowIds[cellKey(rowId, columnId)] ?? []}
                                       cellFields={cellFields}
                                       isEditable={isEditable}
                                       minSubRowsPerCell={minSubRowsPerCell}
                                       maxSubRowsPerCell={maxSubRowsPerCell}
                                       otherValues={otherValues}
                                       displayValues={displayValues}
                                       pickedDisplayValues={pickedDisplayValues}
                                       loadedValues={loadedValues}
                                       recordLabels={recordLabels}
                                       subRowLabel={subRowLabel}
                                       onCommit={handleCommit}
                                       onPickDisplayValue={onPickDisplayValue}
                                       onAddSubRow={handleAddSubRow}
                                       onRemoveSubRow={handleRemoveSubRow}
                                    />
                                 </Box>
                              ))}
                              {mayAddColumn && <Box component="td" sx={cellSX} />}
                              <Box component="td" sx={{...cellSX, ...SPACER_CELL_SX}} />
                           </Box>
                        ))}

                        {mayAddRow && (
                           <Box component="tr">
                              <Box component="td" sx={{...cellSX, ...stickyCellSX}}>
                                 <Button onClick={() => handleAddRow(gridId)} sx={addAxisButtonSX}>{`+ Add ${rowLabel}`}</Button>
                              </Box>
                              {axes.columnIds.map(columnId => <Box component="td" key={columnId} sx={cellSX} />)}
                              {mayAddColumn && <Box component="td" sx={cellSX} />}
                              <Box component="td" sx={{...cellSX, ...SPACER_CELL_SX}} />
                           </Box>
                        )}
                     </Box>
                  </Box>
               </Box>
            </Box>
         </React.Fragment>
      );
   }


   ///////////////////////////////////////////////////////////////////////////////////
   // built once per render, since the alert container's margin depends on whether  //
   // there are any.  the count is left where the post-save scroll can read it.     //
   //                                                                               //
   // A spot to optimize if rendering ever gets slow enough to complain about, and  //
   // deliberately not optimized before then.  Both calls scan the whole structure  //
   // (missing values, three unique-key passes, grid header overlaps, and a full    //
   // sortAxis copy plus two JSON.stringify per sort rule per grid inside           //
   // isAxisSorted) - and this component does NOT re-render only when its own       //
   // values change: FormikAwareDashboardWidgets subscribes to the record screen's  //
   // Formik values, so a keystroke in ANY field on the screen gets here.  Wrapping //
   // these in a useMemo keyed on [structure, committedValues, submitAttempted] is  //
   // the fix; the per-column {...headerCellSX, ...headerBackgroundSX} spreads in   //
   // renderGrid are the other half of it.  No performance complaint has been       //
   // raised in real use, so the simpler code stands.                               //
   ///////////////////////////////////////////////////////////////////////////////////
   const missingValues = missingRequiredValues();
   const alerts        = buildAlerts(missingValues);

   missingCountRef.current = missingValues.length;

   return (
      ////////////////////////////////////////////////////////////////////////////////////
      // the table's bottom border is the last thing in the card, so the card's own     //
      // padding below it would read as a strip of empty space under a finished table - //
      // which is not what a child-record-list does either: RecordGridWidget cancels    //
      // the same padding with a negative margin on its own container.                  //
      //                                                                                //
      // !important because a record screen sets this element's padding through a       //
      // descendant selector, which outranks the Card's own emitted rule.               //
      //                                                                                //
      // Only while there IS a table: the unmet-prerequisite and no-grids messages      //
      // below are plain text that has no padding of its own, and need the card's.      //
      ////////////////////////////////////////////////////////////////////////////////////
      <Widget widgetMetaData={widgetMetaData} cardAdditionalSx={unmetPrerequisites.length > 0 || showsNoGridsMessage ? {} : {paddingBottom: "0 !important"}}>
         <Box>
            <GridBuilderFormikBridge valuesRef={valuesRef} setFieldValueRef={setFieldValueRef} onValuesChanged={onValuesChanged} onSubmitAttempted={handleSubmitAttempted} />

            {unmetPrerequisites.length > 0 ? (
               ///////////////////////////////////////////////////////////////////////////////
               // styled as the app's other empty states are (see TableCard's no-rows-found //
               // text).  no padding of its own - the Widget wrapper already supplies 24px. //
               ///////////////////////////////////////////////////////////////////////////////
               <MDTypography variant="subtitle2" color="secondary" fontWeight="regular">
                  {prerequisiteMessage}
               </MDTypography>
            ) : (
               <>
                  {/****************************************************************************
                    * the alerts.  gap rather than a margin on each, so an alert animating in
                    * grows the stack by exactly its own height; and the container's own bottom
                    * margin only exists while there is something in it.
                    ****************************************************************************/}
                  <Box ref={alertsRef} display="flex" flexDirection="column" gap="0.5rem" mb={alerts.length > 0 ? "0.5rem" : 0}>
                     {alerts}
                  </Box>

                  {/****************************************************************************
                    * one section per grid.  With a single anonymous grid this is one Fragment
                    * holding one table wrapper, which is exactly what stood here before grids
                    * existed - the loop degenerates rather than being branched around.
                    ****************************************************************************/}
                  {structure.gridIds.map((gridId, gridIndex) => renderGrid(gridId, gridIndex))}

                  {showsNoGridsMessage && (
                     //////////////////////////////////////////////////////////////////////////////
                     // in place of the grids, not alongside them - the loop above emitted       //
                     // nothing.  Same treatment as the unmet-prerequisite message, which is the //
                     // app's other empty state (see TableCard's no-rows-found text).            //
                     //////////////////////////////////////////////////////////////////////////////
                     <MDTypography variant="subtitle2" color="secondary" fontWeight="regular">
                        {noGridsMessage(gridLabel)}
                     </MDTypography>
                  )}

                  {mayAddGrid && (
                     ////////////////////////////////////////////////////////////////////////////////////
                     // below the last grid, and outside the table bleed - this is a control for the   //
                     // widget rather than a cell of any one grid.  The card's padding-bottom is       //
                     // removed for the flush table edge, so the spacing under it is stated here.      //
                     //                                                                                //
                     // Outside the per-grid loop as well, which is what keeps it reachable when there //
                     // are no grids at all: a user who removed their last one could otherwise never   //
                     // add another.                                                                   //
                     ////////////////////////////////////////////////////////////////////////////////////
                     <Box mt="0.75rem" mb="0.75rem">
                        <Button onClick={handleAddGrid} sx={addAxisButtonSX}>{`+ Add ${gridLabel}`}</Button>
                     </Box>
                  )}
               </>
            )}
         </Box>
      </Widget>
   );
}
